import React from 'react';
import { PageElement, ElementType, QuizConfig } from '../types';
import { SEAL_ICONS } from './icons/SealIcons';
import { WhatsAppAudioPlayer } from './WhatsAppAudioPlayer';

import RouletteElement from './RouletteElement';
import SlotMachineElement from './SlotMachineElement';

interface ElementRendererProps {
  element: PageElement;
  config: QuizConfig;
  onOptionSelect?: (optionId: string | string[]) => void;
  selectedOption?: string | string[];
  multiple?: boolean;
  previewMode?: boolean;
  onNext?: () => void;
  onFormChange?: (values: Record<string, any>) => void;
  formValues?: Record<string, any>;
  globalFormValues?: Record<string, any>;
  disableInterpolation?: boolean;
  invalidFieldIds?: string[];
  validationMessage?: string;
  onButtonAction?: (action: 'next' | 'redirect', redirectUrl?: string, options?: { target?: '_self' | '_blank' }) => void;
  priceCheckboxes?: Record<string, boolean>;
  onPriceCheckboxChange?: (itemId: string, checked: boolean) => void;
  isRedirecting?: boolean;
  addUTMsToUrl?: (url: string) => string;
  coins?: number;
  onConfetti?: () => void;
  ignoreTimers?: boolean;
}

interface EmbedRendererProps {
  code: string;
  className?: string;
  previewMode?: boolean;
  aspectRatio?: string; // ex: '16:9', '9:16', '4:3', '1:1'
}

const EmbedRenderer: React.FC<EmbedRendererProps> = ({ code, className, previewMode = false, aspectRatio = '16:9' }) => {
  // Criar o conteúdo do iframe - isolamento total como HTML puro
  const iframeContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; }
  </style>
</head>
<body>
${code}
</body>
</html>`;

  // Converter '16:9' para '16/9' para CSS aspect-ratio
  const cssAspectRatio = aspectRatio.replace(':', '/');
  const isVertical = aspectRatio === '9:16';

  // NO EDITOR: mostrar o vídeo com overlay para permitir seleção
  if (!previewMode) {
    return (
      <div className={`${className || ''} relative`} style={isVertical ? { maxWidth: '360px', margin: '0 auto' } : undefined}>
        <iframe
          srcDoc={iframeContent}
          style={{
            width: '100%',
            aspectRatio: cssAspectRatio,
            border: 'none',
            display: 'block'
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
        {/* Overlay transparente para capturar cliques e permitir seleção do elemento */}
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          style={{ background: 'transparent' }}
        />
      </div>
    );
  }

  // NO PREVIEW/VIEWER: iframe direto sem overlay
  return (
    <div style={isVertical ? { maxWidth: '360px', margin: '0 auto' } : undefined}>
      <iframe
        srcDoc={iframeContent}
        className={className}
        style={{
          width: '100%',
          aspectRatio: cssAspectRatio,
          border: 'none',
          display: 'block'
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
};

const FbCommentsRenderer: React.FC<{
  element: PageElement;
  previewMode: boolean;
  isDelayVisible: boolean;
  mbRem: number;
  globalRadius: number;
  interpolate: (s: string) => string;
}> = ({ element, previewMode, isDelayVisible, mbRem, globalRadius, interpolate }) => {
  if (previewMode && !isDelayVisible) return null;

  const comments = (element as any).comments || [];
  const totalComments = (element as any).totalComments || comments.length;
  const showActions = (element as any).showActions === true;

  // Calcular borderClass e shadowClass baseado nas propriedades do elemento
  const borderSize = (element as any).border || 'small';
  const shadowSize = (element as any).shadow || 'none';
  const borderClass = borderSize === 'none' ? 'border-0' : borderSize === 'small' ? 'border' : borderSize === 'medium' ? 'border-2' : 'border-4';
  const shadowClass = shadowSize === 'none' ? '' : shadowSize === 'small' ? 'shadow-sm' : shadowSize === 'medium' ? 'shadow' : 'shadow-lg';

  const [currentCommentIndex, setCurrentCommentIndex] = React.useState(0);
  const [commentProgress, setCommentProgress] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);

  React.useEffect(() => {
    if (comments.length <= 1) return;

    // Reset: tira a animação e vai pra 0%
    setIsAnimating(false);
    setCommentProgress(0);

    let nextTimeout: any;

    // Aguarda apenas pra garantir o reflow da largura 0% no DOM antes de transitar pra 100%
    const initDelay = setTimeout(() => {
      setIsAnimating(true);
      setCommentProgress(100);

      const durationMs = ((element as any).commentsDuration || 20) * 1000;
      nextTimeout = setTimeout(() => {
        setCurrentCommentIndex(prev => (prev + 1) % comments.length);
      }, durationMs);

    }, 50);

    return () => {
      clearTimeout(initDelay);
      clearTimeout(nextTimeout);
    };
  }, [currentCommentIndex, comments.length, (element as any).commentsDuration]);

  const currentComment = comments[currentCommentIndex];
  if (!currentComment) return null;

  return (
    <div style={{ marginBottom: `${mbRem}rem` }}>
      <div className={`bg-white rounded-lg border border-slate-200 overflow-hidden ${borderClass} ${shadowClass}`} style={{ maxWidth: '100%', fontFamily: 'Helvetica, Arial, sans-serif' }}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="bg-[#3b5998] text-white rounded-sm w-5 h-5 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </div>
            <span className="text-[#3b5998] font-medium text-sm">Plugin Social Facebook</span>
          </div>
          <div className="text-slate-500 text-xs">
            {currentCommentIndex + 1} {(element as any).textOf || 'of'} {totalComments} {(element as any).textComments || 'comments'}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-slate-100 w-full mb-0 overflow-hidden">
          <div
            className="h-full ease-linear border-r border-[#d1d5db]"
            style={{
              width: `${commentProgress}%`,
              backgroundColor: '#d1d5db',
              transition: isAnimating ? `width ${((element as any).commentsDuration || 20)}s linear` : 'none'
            }}
          />
        </div>

        {/* Comment Body */}
        <div className="p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              {currentComment.avatarUrl ? (
                <img src={currentComment.avatarUrl} alt={currentComment.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-bold text-sm" style={{ color: '#000000' }}>{currentComment.name}</span>
                <span className="text-slate-400 text-xs">{currentComment.timeAgo}</span>
              </div>
              <div className="text-slate-800 text-sm leading-snug mb-2">
                {interpolate(currentComment.content)}
              </div>
              {currentComment.imageUrl && (
                <div className="mt-2">
                  <img src={currentComment.imageUrl} alt="Comment attachment" className="rounded-lg max-w-full max-h-60 object-cover border border-slate-200" />
                </div>
              )}
              {showActions && (
                <div className="flex gap-3 mt-2 text-xs text-slate-500 font-medium">
                  <span className="cursor-pointer hover:underline text-[#3b5998]">Curtir</span>
                  <span className="cursor-pointer hover:underline text-[#3b5998]">Responder</span>
                  <span className="cursor-pointer hover:underline text-[#3b5998]">Compartilhar</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ElementRenderer: React.FC<ElementRendererProps> = ({
  element,
  config,
  onOptionSelect,
  selectedOption,
  multiple = false,
  onNext,
  previewMode = false,
  onFormChange,
  formValues,
  globalFormValues,
  disableInterpolation = false,
  invalidFieldIds,
  validationMessage,
  onButtonAction,
  isRedirecting = false,
  addUTMsToUrl,
  priceCheckboxes = {},
  onPriceCheckboxChange,
  coins = 0,
  onConfetti,
  ignoreTimers = false,
}) => {
  // Inject shake animation style
  React.useEffect(() => {
    const id = 'input-shake-style';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = `
        @keyframes input-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .input-shake {
          animation: input-shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
        }
      `;
      document.head.appendChild(el);
    }
  }, []);

  const [shake, setShake] = React.useState(false);
  React.useEffect(() => {
    if (Array.isArray(invalidFieldIds) && invalidFieldIds.length > 0) {
      setShake(true);
      const t = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(t);
    }
  }, [invalidFieldIds]);

  const sizeScale = (config.elementSize === 'medium' ? 1.15 : config.elementSize === 'large' ? 1.3 : config.elementSize === 'extra' ? 1.5 : 1);
  const scaledOptionFont = `calc(${config.fontSizes.option} * ${sizeScale})`;
  const mbRem = (config.spacing === 'none' ? 0 : config.spacing === 'simple' ? 0.5 : config.spacing === 'medium' ? 0.75 : config.spacing === 'large' ? 1 : 1.25);
  const br = (config.borderRadius || 'light');
  const globalRadius = (br === 'none' ? 0 : br === 'light' ? 8 : br === 'medium' ? 12 : 16);
  // Helper para obter valor de uma variável
  const getFormValue = (key: string): string => {
    if (!globalFormValues) return '';
    const val = globalFormValues[key];
    if (val && typeof val === 'object' && 'value' in val) {
      return String(val.value || '');
    }
    if (typeof val === 'string') {
      return val;
    }
    return '';
  };

  const interpolate = (s: string | undefined) => {
    const src = String(s || '');
    if (!previewMode) {
      // No editor, mostrar variáveis destacadas (inclusive as com transformação)
      return src
        .replace(/\{pontos-acumulados\}/g, `<span style="background-color: rgba(234,179,8,0.16); color: #ca8a04; padding: 0 4px; border-radius: 4px; border: 1px dashed #ca8a04">pontos</span>`)
        .replace(/\{(uppercase|lowercase|capitalize):([a-zA-Z0-9_-]+)\}/g, (m) => `<span style="background-color: rgba(14,165,233,0.16); color: #0ea5b7; padding: 0 4px; border-radius: 4px; border: 1px dashed #0ea5b7">${m}</span>`)
        .replace(/\{([a-zA-Z0-9_-]+)\}/g, (m) => m === '{pontos-acumulados}' ? m : `<span style="background-color: rgba(14,165,233,0.16); color: #0ea5b7; padding: 0 4px; border-radius: 4px; border: 1px dashed #0ea5b7">${m}</span>`);
    }
    return src
      .replace(/\{pontos-acumulados\}/g, String(coins))
      // Transformações: {uppercase:var}, {lowercase:var}, {capitalize:var}
      .replace(/\{(uppercase|lowercase|capitalize):([a-zA-Z0-9_-]+)\}/g, (_, transform, key) => {
        const value = getFormValue(key);
        switch (transform) {
          case 'uppercase':
            return value.toUpperCase();
          case 'lowercase':
            return value.toLowerCase();
          case 'capitalize':
            return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
          default:
            return value;
        }
      })
      // Variáveis simples: {var}
      .replace(/\{([a-zA-Z0-9_-]+)\}/g, (_, k) => getFormValue(k));
  };

  const showDelay = Math.max(0, Number(((element as any).showAfterSeconds || 0)));
  const [isDelayVisible, setIsDelayVisible] = React.useState(!previewMode || showDelay === 0 || ignoreTimers);
  React.useEffect(() => {
    if (!previewMode || showDelay === 0 || ignoreTimers) { setIsDelayVisible(true); return; }
    setIsDelayVisible(false);
    const t = setTimeout(() => setIsDelayVisible(true), showDelay * 1000);
    return () => clearTimeout(t);
  }, [previewMode, element.id, showDelay, ignoreTimers]);

  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    if (element.type === ElementType.Image && (element as any).url) {
      // Se o elemento de imagem já existe e já carregou (cache), marca como carregado
      if (imgRef.current && imgRef.current.complete) {
        setImageLoaded(true);
      } else {
        setImageLoaded(false);
      }
    }
  }, [element.id, (element as any).url]);

  const sanitizeInput = (input: string) => {
    if (typeof input !== 'string') return '';
    // Escapa HTML para prevenir XSS e remove caracteres perigosos de SQL.
    return input
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
      .replace(/--/g, '')
      .replace(/;/g, '');
  };

  const renderElement = () => {
    switch (element.type) {
      case ElementType.Roulette:
        if (previewMode && !isDelayVisible) return null;
        return (
          <RouletteElement
            element={element as any}
            config={config}
          />
        );

      case ElementType.SlotMachine:
        if (previewMode && !isDelayVisible) return null;
        return (
          <SlotMachineElement
            element={element as any}
            config={config}
            onWin={() => {
              if (onConfetti) onConfetti();
            }}
            previewMode={previewMode}
          />
        );

      case ElementType.Title:
        const titleAlign = (element as any).align || 'center';
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <h1
              className="font-bold"
              style={{
                fontSize: (element as any).fontSize || config.fontSizes.title,
                lineHeight: (element as any).lineHeight || undefined,
                fontFamily: config.fontFamily,
                color: config.colors.title || config.colors.primary,
                textAlign: titleAlign
              }}
              dangerouslySetInnerHTML={{ __html: interpolate((element as any).content || '') }}
            />
          </div>
        );

      case ElementType.Text:
        const textSize = (element as any).size || 'normal';
        const textScale = textSize === 'small' ? 0.85 : textSize === 'large' ? 1.3 : textSize === 'huge' ? 1.6 : 1;
        const txtAlign = (element as any).align || 'center';
        const baseFontSize = (element as any).fontSize || config.fontSizes.subtitle;
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <p
              style={{
                fontSize: `calc(${baseFontSize} * ${textScale})`,
                fontFamily: config.fontFamily,
                color: config.colors.text || '#374151',
                textAlign: txtAlign,
                whiteSpace: 'pre-line'
              }}
              dangerouslySetInnerHTML={{ __html: interpolate((element as any).content || '') }}
            />
          </div>
        );

      case ElementType.Alert:
        const softColors = {
          info: 'bg-blue-50 border-blue-200 text-blue-800',
          warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
          success: 'bg-green-50 border-green-200 text-green-800',
          error: 'bg-red-50 border-red-200 text-red-800',
          neutral: 'bg-slate-100 border-slate-200 text-slate-800'
        } as const;
        const solidColors = {
          info: 'bg-blue-500 border-blue-500 text-white',
          warning: 'bg-yellow-500 border-yellow-500 text-black',
          success: 'bg-green-500 border-green-500 text-white',
          error: 'bg-red-500 border-red-500 text-white',
          neutral: 'bg-slate-500 border-slate-500 text-white'
        } as const;
        const variant = element.variant || 'error';
        const colorClass = (element as any).solid ? solidColors[variant] : softColors[variant];
        const padClass = (element as any).padding === 'small' ? 'p-2' : (element as any).padding === 'large' ? 'p-6' : 'p-4';
        const borderStyle = (element as any).borderStyle || 'solid';
        const alertBorderClass = borderStyle === 'dashed' ? 'border-2 border-dashed' : 'border border-solid';

        const dashedBorderColor = (borderStyle === 'dashed' && !(element as any).solid) ? {
          info: 'border-blue-400',
          warning: 'border-yellow-400',
          success: 'border-green-400',
          error: 'border-red-400',
          neutral: 'border-slate-400'
        }[variant] : '';

        if (previewMode && !isDelayVisible) return null;
        return (
          <div className={`quiz-alert ${padClass} ${alertBorderClass} ${colorClass} ${dashedBorderColor}`}
            style={{ borderRadius: globalRadius, marginBottom: `${mbRem}rem` }}>
            <div style={{ whiteSpace: 'pre-line', textAlign: (element as any).align || 'left' }} dangerouslySetInnerHTML={{ __html: interpolate((element as any).content || '') }} />
          </div>
        );

      case ElementType.Timer:
        const softTimerColors = {
          info: 'bg-blue-50 border-blue-200 text-blue-800',
          warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
          success: 'bg-green-50 border-green-200 text-green-800',
          error: 'bg-red-50 border-red-200 text-red-800',
          neutral: 'bg-slate-100 border-slate-200 text-slate-800'
        } as const;
        const solidTimerColors = {
          info: 'bg-blue-500 border-blue-500 text-white',
          warning: 'bg-yellow-500 border-yellow-500 text-black',
          success: 'bg-green-500 border-green-500 text-white',
          error: 'bg-red-500 border-red-500 text-white',
          neutral: 'bg-slate-500 border-slate-500 text-white'
        } as const;
        const timerVariant = (element as any).variant || 'error';
        const timerColorClass = (element as any).solid ? solidTimerColors[timerVariant] : softTimerColors[timerVariant];
        const timerPadClass = (element as any).padding === 'small' ? 'p-2' : (element as any).padding === 'large' ? 'p-6' : 'p-4';
        const initialTotal = Math.max(0, (element as any).durationSeconds || 0);
        const [remaining, setRemaining] = React.useState(initialTotal);
        React.useEffect(() => {
          const total = Math.max(0, (element as any).durationSeconds || 0);
          setRemaining(total);
          if (!previewMode) return;
          if (total <= 0) return;
          const iv = setInterval(() => {
            setRemaining(r => (r > 0 ? r - 1 : 0));
          }, 1000);
          return () => clearInterval(iv);
        }, [previewMode, element.id, (element as any).durationSeconds]);
        const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
        const ss = String(remaining % 60).padStart(2, '0');
        const formatted = `${mm}:${ss}`;
        const baseContent = (element as any).content || '';
        const htmlContent = baseContent.includes('[time]') ? baseContent.replace(/\[time\]/g, formatted) : `${baseContent} ${formatted}`;
        if (previewMode && !isDelayVisible) return null;
        return (
          <div className={`${timerPadClass} border ${timerColorClass}`}
            style={{ borderRadius: globalRadius, marginBottom: `${mbRem}rem`, textAlign: (element as any).align || 'center' }}>
            <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: interpolate(htmlContent) }} />
          </div>
        );

      case ElementType.Options:
        const elementMultiple = element.multiple || multiple;
        const layout = (element as any).layout || 'grid2';
        const styleVariant = (element as any).style || 'simple';

        const checkStyle = (element as any).checkStyle || 'traditional';
        const checkPosition = (element as any).checkPosition || 'before';
        const optTextAlign = (element as any).textAlign || 'left';
        const fontWeight = (element as any).fontWeight || 'medium';
        const fontWeightValue = fontWeight === 'medium' ? '500' :
          fontWeight === 'black' ? '900' :
            'normal';
        const borderSize = (element as any).border || 'medium';
        const shadowSize = (element as any).shadow || 'none';
        const spacingSize = (element as any).spacing || 'medium';
        const borderRadius = globalRadius;
        const imageOrientation = (element as any).imageOrientation || 'horizontal';
        const verticalImageRatio = (element as any).verticalImageRatio || '1:1';
        const mediaSize = (element as any).mediaSize || 'small';
        const emojiScale = mediaSize === 'medium' ? 1.15 : mediaSize === 'large' ? 1.3 : mediaSize === 'extra' ? 1.5 : 1;
        const imageScale = mediaSize === 'medium' ? 0.8 : mediaSize === 'large' ? 1.0 : mediaSize === 'extra' ? 1.3 : 0.6;
        const ratioMap: Record<string, string> = { '1:1': '1 / 1', '16:9': '16 / 9', '4:3': '4 / 3', '9:16': '9 / 16' };
        React.useEffect(() => {
          const id = 'opt-extras-style';
          if (document.getElementById(id)) return;
          const el = document.createElement('style');
          el.id = id;
          el.textContent = `
            .opt-emboss::before { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: var(--opt-emboss-h, 0.5rem); background: rgba(var(--opt-color-dark-rgb, 0,0,0), 0.4); border-bottom-left-radius: inherit; border-bottom-right-radius: inherit; pointer-events: none; }
            .opt-ring:hover { box-shadow: 0 0 0 2px var(--opt-ring-color, #3b82f6) !important; }
            .opt-ring-selected { box-shadow: 0 0 0 2px var(--opt-ring-color, #3b82f6) !important; }
            .opt-border:hover { border-color: var(--opt-border-color, #3b82f6) !important; }
            .opt-border-selected { border-color: var(--opt-border-color, #3b82f6) !important; }
          `;
          document.head.appendChild(el);
        }, []);
        const toRgbOpt = (hex: string) => {
          const h = (hex || '').replace('#', '');
          const m = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
          const r = parseInt(m.slice(0, 2), 16) || 0;
          const g = parseInt(m.slice(2, 4), 16) || 0;
          const b = parseInt(m.slice(4, 6), 16) || 0;
          return `${r}, ${g}, ${b}`;
        };
        const toRgbObjOpt = (hex: string) => {
          const h = (hex || '').replace('#', '');
          const m = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
          return {
            r: parseInt(m.slice(0, 2), 16) || 0,
            g: parseInt(m.slice(2, 4), 16) || 0,
            b: parseInt(m.slice(4, 6), 16) || 0,
          };
        };
        const darkenRgbOpt = (rgb: { r: number; g: number; b: number }, amt = 40) => `${Math.max(0, rgb.r - amt)}, ${Math.max(0, rgb.g - amt)}, ${Math.max(0, rgb.b - amt)}`;
        const optColor = config.colors.primary;
        const optEmbossH = (config.elementSize === 'small' ? '0.32rem' : config.elementSize === 'medium' ? '0.36rem' : config.elementSize === 'large' ? '0.4rem' : '0.44rem');

        const containerClass =
          layout === 'list'
            ? 'grid grid-cols-1 gap-2 mt-4'
            : layout === 'grid2'
              ? 'grid grid-cols-2 gap-2 mt-4'
              : layout === 'grid3'
                ? 'grid grid-cols-3 gap-2 mt-4'
                : layout === 'grid4'
                  ? 'grid grid-cols-4 gap-2 mt-4'
                  : 'flex flex-wrap justify-center gap-2 mt-4';
        const optPadClass = spacingSize === 'none'
          ? 'p-1'
          : spacingSize === 'simple'
            ? 'p-2'
            : spacingSize === 'medium'
              ? 'p-3'
              : spacingSize === 'large'
                ? 'p-4'
                : 'p-6';
        const labelPaddingRem = (spacingSize === 'none' ? 0.25 : spacingSize === 'simple' ? 0.5 : spacingSize === 'medium' ? 0.75 : spacingSize === 'large' ? 1 : 1.5) * sizeScale;
        const borderClass = borderSize === 'none' ? 'border-0' : borderSize === 'small' ? 'border' : borderSize === 'medium' ? 'border-2' : 'border-4';
        const shadowClass = shadowSize === 'none' ? '' : shadowSize === 'small' ? 'shadow-sm' : shadowSize === 'medium' ? 'shadow' : 'shadow-lg';

        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            {(element as any).title && (
              <h1
                className="font-bold"
                style={{
                  fontSize: config.fontSizes.title,
                  fontFamily: config.fontFamily,
                  color: config.colors.title || config.colors.primary,
                  textAlign: 'center',
                  marginBottom: `${mbRem}rem`
                }}
                dangerouslySetInnerHTML={{ __html: interpolate((element as any).title || '') }}
              />
            )}
            {(element as any).description && (
              <div
                style={{
                  fontSize: config.fontSizes.subtitle,
                  fontFamily: config.fontFamily,
                  color: config.colors.text || '#374151',
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                  marginBottom: `calc(${mbRem}rem + 20px)`
                }}
                dangerouslySetInnerHTML={{ __html: interpolate((element as any).description || '') }}
              />
            )}
            <div className={containerClass}>
              {element.options.map((option, index) => {
                const selectedList = Array.isArray(selectedOption)
                  ? selectedOption
                  : typeof selectedOption === 'string'
                    ? selectedOption.split(',')
                    : [];
                const isChecked = elementMultiple
                  ? selectedList.includes(option.id)
                  : selectedList.length ? selectedList[0] === option.id : false;


                const rgbPrimary = toRgbOpt(config.colors.primary);
                const primarySoftBg = `rgba(${rgbPrimary}, 0.1)`;
                const primaryBorderColor = `rgba(${rgbPrimary}, 0.5)`;
                const computedBorderClass = styleVariant === 'primary' ? '' : 'border-slate-300';
                const computedBorderStyle = styleVariant === 'primary' ? {
                  borderColor: primaryBorderColor
                } : {};

                const baseBg = styleVariant === 'highlight' ? config.colors.primary :
                  styleVariant === 'gray' ? '#e2e8f0' :
                    styleVariant === 'relief' ? (config.colors.primary || '#0ea5e9') :
                      styleVariant === 'primary' ? primarySoftBg :
                        '#ffffff';
                const baseColor = (styleVariant === 'highlight' || styleVariant === 'relief') ? '#ffffff' : (config.colors.text || '#374151');
                const labelColor = (typeof (element as any).textColor === 'string' && (element as any).textColor) ? (element as any).textColor : baseColor;
                const optAnim = (element as any).animation || 'none';
                const animClass = optAnim === 'zoom' ? 'transition-transform duration-200 hover:scale-[1.05]' :
                  optAnim === 'border' ? 'opt-border' :
                    optAnim === 'border_zoom' ? 'transition-transform duration-200 hover:scale-[1.01] opt-border' :
                      '';
                const ringClass = isChecked ? 'opt-border-selected' : '';
                const mediaType = option.mediaType || (option.imageUrl ? 'image' : option.emoji ? 'emoji' : 'none');
                const hasEmoji = mediaType === 'emoji' && option.emoji;
                const hasImage = mediaType === 'image' && option.imageUrl;
                const hasCheck = checkStyle !== 'none';
                const hasSideBox = hasCheck || hasEmoji || (hasImage && imageOrientation !== 'vertical');
                const itemsCount = (hasEmoji ? 1 : 0) + (hasImage ? 1 : 0) + (hasCheck ? 1 : 0);
                const baseMarginRem = hasEmoji ? 0.1 : 0.25; // margem mínima quando há emoji
                const textGapRem = hasEmoji ? (hasCheck ? 1.0 : 0.6) : (hasCheck ? 0.75 : 0.4);
                const emojiWRem = 2.0 * emojiScale;
                const imageWRem = 2.5 * imageScale;
                const checkWRem = 1.5;
                const gapRem = 0.25; // gap menor entre emoji e checkbox
                const sizeSumRem = (hasEmoji ? emojiWRem : 0) + (hasImage ? imageWRem : 0) + (hasCheck ? checkWRem : 0);
                const gapsRem = Math.max(itemsCount - 1, 0) * gapRem;
                const sidePadding = `${(hasSideBox ? (baseMarginRem + sizeSumRem + gapsRem + textGapRem) : baseMarginRem).toFixed(2)}rem`;
                const sidePosClass = checkPosition === 'after' ? 'right-1' : (hasEmoji ? 'left-0' : 'left-1');

                // Logic for Split Mode (Emoji Left, Checkbox Right)
                const isSplitMode = checkPosition === 'after' && (hasEmoji || (hasImage && imageOrientation !== 'vertical'));

                const paddingLeftCalculated = isSplitMode
                  ? `${(baseMarginRem + (hasEmoji ? emojiWRem : 0) + (hasImage ? imageWRem : 0) + ((hasEmoji || hasImage) ? 0.8 : 0)).toFixed(2)}rem`
                  : (checkPosition === 'before' ? sidePadding : '0.5rem');

                const paddingRightCalculated = isSplitMode
                  ? `${(baseMarginRem + (hasCheck ? checkWRem : 0) + (hasCheck ? 0.75 : 0)).toFixed(2)}rem`
                  : (checkPosition === 'after' ? sidePadding : '1rem');
                const checkNode = (
                  checkStyle === 'none' ? null : (
                    checkStyle === 'arrow' ? (
                      (() => {
                        const arrowBg = (styleVariant === 'highlight' || styleVariant === 'relief') ? (config.colors.primary || '#0ea5e9') : styleVariant === 'gray' ? '#ffffff' : '#ffffff';
                        const arrowBorder = (styleVariant === 'highlight' || styleVariant === 'relief') ? (config.colors.primary || '#0ea5e9') : styleVariant === 'gray' ? '#cbd5e1' : '#cbd5e1';
                        const arrowColor = (styleVariant === 'highlight' || styleVariant === 'relief') ? '#ffffff' : (config.colors.text || '#374151');
                        return (
                          <div className="rounded-full border flex items-center justify-center" style={{ width: `${1.5 * sizeScale}rem`, height: `${1.5 * sizeScale}rem`, backgroundColor: arrowBg, borderColor: arrowBorder }}>
                            <svg style={{ width: `${1.0 * sizeScale}rem`, height: `${1.0 * sizeScale}rem` }} viewBox="0 0 24 24" fill="none" stroke={arrowColor} strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        );
                      })()
                    ) : checkStyle === 'traditional' ? (
                      <div className="rounded-full border-2 flex items-center justify-center transition-all duration-200" style={{ width: `${1.5 * sizeScale}rem`, height: `${1.5 * sizeScale}rem`, backgroundColor: isChecked ? (config.colors.primary || '#0ea5e9') : '#ffffff', borderColor: isChecked ? (config.colors.primary || '#0ea5e9') : '#cbd5e1' }}>
                        {isChecked && (
                          <svg className="text-white" style={{ width: `${1.0 * sizeScale}rem`, height: `${1.0 * sizeScale}rem` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    ) : checkStyle === 'square' ? (
                      <div className="rounded-sm border-2 flex items-center justify-center transition-all duration-200" style={{ width: `${1.5 * sizeScale}rem`, height: `${1.5 * sizeScale}rem`, backgroundColor: isChecked ? (config.colors.primary || '#0ea5e9') : '#ffffff', borderColor: isChecked ? (config.colors.primary || '#0ea5e9') : '#cbd5e1' }}>
                        {isChecked && (
                          <svg className="text-white" style={{ width: `${1.0 * sizeScale}rem`, height: `${1.0 * sizeScale}rem` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    ) : checkStyle === 'number' ? (
                      <div className="flex items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700"
                        style={{ width: `${1.5 * sizeScale}rem`, height: `${1.5 * sizeScale}rem`, fontSize: `${0.75 * sizeScale}rem` }}>{String(index + 1)}</div>
                    ) : (
                      <div className="flex items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700"
                        style={{ width: `${1.5 * sizeScale}rem`, height: `${1.5 * sizeScale}rem`, fontSize: `${0.75 * sizeScale}rem` }}>{String.fromCharCode(65 + index)}</div>
                    )
                  )
                );

                if (hasImage && imageOrientation === 'vertical') {
                  return (
                    <label
                      key={option.id}
                      onClick={(e) => {
                        if (!onOptionSelect) return;
                        e.preventDefault();
                        if (elementMultiple) {
                          const cur = selectedList;
                          const next = isChecked ? cur.filter(id => id !== option.id) : [...cur, option.id];
                          onOptionSelect(next);
                        } else {
                          onOptionSelect(option.id);
                        }
                      }}
                      className={`cursor-pointer ${borderClass} ${computedBorderClass} ${shadowClass} ${ringClass} ${styleVariant === 'relief' ? 'opt-emboss relative overflow-hidden' : ''} ${animClass}`}
                      style={{ ...computedBorderStyle, borderRadius, backgroundColor: baseBg, color: labelColor, textAlign: optTextAlign, ['--opt-ring-color' as any]: (config.colors.primary || '#0ea5e9'), ['--opt-border-color' as any]: (config.colors.primary || '#0ea5e9'), ...(styleVariant === 'relief' ? { ['--opt-color-rgb' as any]: toRgbOpt(optColor), ['--opt-color-dark-rgb' as any]: darkenRgbOpt(toRgbObjOpt(optColor)), ['--opt-emboss-h' as any]: optEmbossH } : {}) }}
                    >
                      <input
                        type={elementMultiple ? 'checkbox' : 'radio'}
                        name={element.id}
                        value={option.id}
                        checked={isChecked}
                        onChange={() => { }}
                        className="sr-only"
                      />
                      <div className="w-full overflow-hidden" style={{ borderRadius }}>
                        <img
                          src={option.imageUrl as string}
                          alt=""
                          style={{
                            width: '100%',
                            display: 'block',
                            ...(verticalImageRatio === 'auto'
                              ? { height: 'auto' }
                              : { aspectRatio: ratioMap[verticalImageRatio] || '1 / 1', objectFit: 'cover' })
                          }}
                        />
                      </div>
                      {checkPosition === 'after' ? (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            alignItems: 'center',
                            paddingTop: `${labelPaddingRem}rem`,
                            paddingBottom: `${labelPaddingRem}rem`,
                            paddingLeft: `${labelPaddingRem}rem`,
                            paddingRight: `${labelPaddingRem}rem`
                          }}
                        >
                          <span
                            style={{
                              fontSize: scaledOptionFont,
                              fontFamily: config.fontFamily,
                              fontWeight: fontWeightValue,
                              color: labelColor,
                              textAlign: 'left'
                            }}
                            dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                          />
                          <div style={{ justifySelf: 'end' }}>{checkNode}</div>
                        </div>
                      ) : (
                        <div
                          className="flex items-center"
                          style={{
                            paddingTop: `${labelPaddingRem}rem`,
                            paddingBottom: `${labelPaddingRem}rem`,
                            justifyContent: optTextAlign === 'right' ? 'flex-end' :
                              optTextAlign === 'center' ? 'center' : 'flex-start',
                            columnGap: `${spacingSize === 'none' ? 0.9 : spacingSize === 'simple' ? 1.0 : spacingSize === 'medium' ? 1.1 : spacingSize === 'large' ? 1.25 : 1.4}rem`
                          }}
                        >
                          {checkNode}
                          <span
                            style={{
                              fontSize: scaledOptionFont,
                              fontFamily: config.fontFamily,
                              fontWeight: fontWeightValue,
                              color: labelColor,
                              textAlign: optTextAlign
                            }}
                            dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                          />
                        </div>
                      )}
                    </label>
                  );
                }

                if (hasEmoji && imageOrientation === 'vertical') {
                  return (
                    <label
                      key={option.id}
                      onClick={(e) => {
                        if (!onOptionSelect) return;
                        e.preventDefault();
                        if (elementMultiple) {
                          const cur = selectedList;
                          const next = isChecked ? cur.filter(id => id !== option.id) : [...cur, option.id];
                          onOptionSelect(next);
                        } else {
                          onOptionSelect(option.id);
                        }
                      }}
                      className={`cursor-pointer ${borderClass} ${computedBorderClass} ${shadowClass} ${ringClass} ${styleVariant === 'relief' ? 'opt-emboss relative overflow-hidden' : ''} ${animClass}`}
                      style={{ ...computedBorderStyle, borderRadius, backgroundColor: baseBg, color: labelColor, textAlign: 'center', ['--opt-ring-color' as any]: (config.colors.primary || '#0ea5e9'), ['--opt-border-color' as any]: (config.colors.primary || '#0ea5e9'), ...(styleVariant === 'relief' ? { ['--opt-color-rgb' as any]: toRgbOpt(optColor), ['--opt-color-dark-rgb' as any]: darkenRgbOpt(toRgbObjOpt(optColor)), ['--opt-emboss-h' as any]: optEmbossH } : {}) }}
                    >
                      <input
                        type={elementMultiple ? 'checkbox' : 'radio'}
                        name={element.id}
                        value={option.id}
                        checked={isChecked}
                        onChange={() => { }}
                        className="sr-only"
                      />
                      <div className="w-full flex flex-col items-center" style={{ padding: `${labelPaddingRem}rem` }}>
                        <span style={{ display: 'inline-block', fontSize: `${3.2 * sizeScale * emojiScale}rem`, lineHeight: `${3.5 * sizeScale * emojiScale}rem` }}>{option.emoji}</span>
                        {checkPosition === 'after' ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', width: '100%', marginTop: `${0.5 * sizeScale}rem` }}>
                            <span
                              style={{ fontSize: scaledOptionFont, fontFamily: config.fontFamily, fontWeight: fontWeightValue, color: labelColor, textAlign: 'center' }}
                              dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                            />
                            <div style={{ justifySelf: 'end' }}>{checkNode}</div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center" style={{ gap: '0.6rem', width: '100%', marginTop: `${0.5 * sizeScale}rem` }}>
                            {checkNode}
                            <span
                              style={{ fontSize: scaledOptionFont, fontFamily: config.fontFamily, fontWeight: fontWeightValue, color: labelColor, textAlign: 'center' }}
                              dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                            />
                          </div>
                        )}
                      </div>
                    </label>
                  );
                }

                if (hasImage && imageOrientation === 'horizontal') {
                  return (
                    <label
                      key={option.id}
                      onClick={(e) => {
                        if (!onOptionSelect) return;
                        e.preventDefault();
                        if (elementMultiple) {
                          const cur = selectedList;
                          const next = isChecked ? cur.filter(id => id !== option.id) : [...cur, option.id];
                          onOptionSelect(next);
                        } else {
                          onOptionSelect(option.id);
                        }
                      }}
                      className={`cursor-pointer ${borderClass} ${computedBorderClass} ${shadowClass} ${ringClass} ${styleVariant === 'relief' ? 'opt-emboss relative overflow-hidden' : ''} ${animClass}`}
                      style={{ ...computedBorderStyle, borderRadius, backgroundColor: baseBg, color: labelColor, textAlign: optTextAlign, ['--opt-ring-color' as any]: (config.colors.primary || '#0ea5e9'), ['--opt-border-color' as any]: (config.colors.primary || '#0ea5e9'), ...(styleVariant === 'relief' ? { ['--opt-color-rgb' as any]: toRgbOpt(optColor), ['--opt-color-dark-rgb' as any]: darkenRgbOpt(toRgbObjOpt(optColor)), ['--opt-emboss-h' as any]: optEmbossH } : {}) }}
                    >
                      <input
                        type={elementMultiple ? 'checkbox' : 'radio'}
                        name={element.id}
                        value={option.id}
                        checked={isChecked}
                        onChange={() => { }}
                        className="sr-only"
                      />
                      <div style={{ display: 'grid', gridTemplateColumns: `${Math.round(120 * sizeScale * imageScale)}px 1fr`, alignItems: 'stretch' }}>
                        <div className="overflow-hidden" style={{ borderTopLeftRadius: borderRadius, borderBottomLeftRadius: borderRadius, minHeight: `${4.5 * sizeScale * imageScale}rem` }}>
                          <img src={option.imageUrl as string} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </div>
                        {checkPosition === 'after' ? (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              alignItems: 'center',
                              paddingTop: `${labelPaddingRem}rem`,
                              paddingBottom: `${labelPaddingRem}rem`,
                              paddingLeft: `${labelPaddingRem * 1.5}rem`,
                              paddingRight: `${labelPaddingRem}rem`
                            }}
                          >
                            <span
                              style={{ fontSize: scaledOptionFont, fontFamily: config.fontFamily, fontWeight: fontWeightValue, color: labelColor, textAlign: optTextAlign }}
                              dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                            />
                            <div style={{ justifySelf: 'end' }}>{checkNode}</div>
                          </div>
                        ) : (
                          <div
                            className="flex items-center"
                            style={{
                              padding: `${labelPaddingRem}rem`,
                              justifyContent: optTextAlign === 'right' ? 'flex-end' :
                                optTextAlign === 'center' ? 'center' : 'flex-start',
                              columnGap: `${spacingSize === 'none' ? 0.9 : spacingSize === 'simple' ? 1.0 : spacingSize === 'medium' ? 1.1 : spacingSize === 'large' ? 1.25 : 1.4}rem`
                            }}
                          >
                            {checkNode}
                            <span
                              style={{ fontSize: scaledOptionFont, fontFamily: config.fontFamily, fontWeight: fontWeightValue, color: labelColor, textAlign: optTextAlign }}
                              dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                            />
                          </div>
                        )}
                      </div>
                    </label>
                  );
                }

                return (
                  <label
                    key={option.id}
                    className={`cursor-pointer ${optPadClass} ${borderClass} ${computedBorderClass} ${shadowClass} ${ringClass} ${styleVariant === 'relief' ? 'opt-emboss relative overflow-hidden' : ''} ${animClass}`}
                    style={{ ...computedBorderStyle, borderRadius, backgroundColor: baseBg, color: labelColor, textAlign: optTextAlign, ['--opt-ring-color' as any]: (config.colors.primary || '#0ea5e9'), ['--opt-border-color' as any]: (config.colors.primary || '#0ea5e9'), ...(styleVariant === 'relief' ? { ['--opt-color-rgb' as any]: toRgbOpt(optColor), ['--opt-color-dark-rgb' as any]: darkenRgbOpt(toRgbObjOpt(optColor)), ['--opt-emboss-h' as any]: optEmbossH } : {}) }}
                    onClick={(e) => {
                      if (!onOptionSelect) return;

                      e.preventDefault();

                      if (elementMultiple) {
                        const cur = selectedList;
                        const next = isChecked ? cur.filter(id => id !== option.id) : [...cur, option.id];
                        onOptionSelect(next);
                      } else {
                        onOptionSelect(option.id);
                      }
                    }}
                  >
                    <div className="relative w-full">
                      {hasSideBox && !(hasEmoji && optTextAlign === 'center') && (
                        <>
                          {/* LEFT SIDE CONTENT (Before Mode OR Split Mode) */}
                          {(checkPosition === 'before' || isSplitMode) && (
                            <div
                              className={`absolute top-1/2 transform -translate-y-1/2 flex items-center gap-1`}
                              style={isSplitMode ? { left: `-${Math.max(labelPaddingRem - 0.25, 0).toFixed(2)}rem` } : (hasEmoji ? { left: `-${Math.max(labelPaddingRem - 0.4, 0).toFixed(2)}rem` } : { left: '0.25rem' })}
                            >
                              <input
                                type={elementMultiple ? 'checkbox' : 'radio'}
                                name={element.id}
                                value={option.id}
                                checked={isChecked}
                                onChange={() => { }}
                                className="sr-only"
                              />
                              {(hasEmoji && (checkPosition === 'before' || isSplitMode)) && (
                                <span style={{ display: 'inline-block', fontSize: `${2 * sizeScale * emojiScale}rem`, lineHeight: `${2.25 * sizeScale * emojiScale}rem` }}>{option.emoji}</span>
                              )}
                              {(hasImage && (checkPosition === 'before' || isSplitMode)) && (
                                <img src={option.imageUrl as string} alt="" className="rounded" style={{ width: `${2.5 * sizeScale * imageScale}rem`, height: `${2.5 * sizeScale * imageScale}rem`, objectFit: 'cover' }} />
                              )}
                              {checkPosition === 'before' && checkNode}
                            </div>
                          )}

                          {/* RIGHT SIDE CONTENT (After Mode) */}
                          {checkPosition === 'after' && (
                            <div
                              className={`absolute top-1/2 transform -translate-y-1/2 right-1 flex items-center gap-1`}
                            >
                              {/* If NOT split mode (normal after), we need the input and other elements here if they weren't rendered on left */}
                              {!isSplitMode && (
                                <input
                                  type={elementMultiple ? 'checkbox' : 'radio'}
                                  name={element.id}
                                  value={option.id}
                                  checked={isChecked}
                                  onChange={() => { }}
                                  className="sr-only"
                                />
                              )}

                              {!isSplitMode && hasEmoji && (
                                <span style={{ display: 'inline-block', fontSize: `${2 * sizeScale * emojiScale}rem`, lineHeight: `${2.25 * sizeScale * emojiScale}rem` }}>{option.emoji}</span>
                              )}
                              {!isSplitMode && hasImage && (
                                <img src={option.imageUrl as string} alt="" className="rounded" style={{ width: `${2.5 * sizeScale * imageScale}rem`, height: `${2.5 * sizeScale * imageScale}rem`, objectFit: 'cover' }} />
                              )}
                              {checkNode}
                            </div>
                          )}
                        </>
                      )}
                      {hasEmoji && optTextAlign === 'center' && (
                        <input
                          type={elementMultiple ? 'checkbox' : 'radio'}
                          name={element.id}
                          value={option.id}
                          checked={isChecked}
                          onChange={() => { }}
                          className="sr-only"
                        />
                      )}
                      <div className="flex items-center" style={{
                        justifyContent: optTextAlign === 'right' ? 'flex-end' :
                          optTextAlign === 'center' ? 'center' : 'flex-start',
                        paddingLeft: (hasEmoji && optTextAlign === 'center') ? '0' : (optTextAlign === 'center' ? sidePadding : ((layout === 'wrap' && !hasSideBox) ? '0.6rem' : paddingLeftCalculated)),
                        paddingRight: (hasEmoji && optTextAlign === 'center') ? '0' : (optTextAlign === 'center' ? sidePadding : ((layout === 'wrap' && !hasSideBox) ? '0.6rem' : paddingRightCalculated)),
                        gap: (hasEmoji && optTextAlign === 'center') ? (hasCheck ? `${spacingSize === 'none' ? 0.5 : spacingSize === 'simple' ? 0.6 : spacingSize === 'medium' ? 0.7 : spacingSize === 'large' ? 0.8 : 1.0}rem` : `${spacingSize === 'none' ? 0.3 : spacingSize === 'simple' ? 0.35 : spacingSize === 'medium' ? 0.4 : spacingSize === 'large' ? 0.45 : 0.5}rem`) : undefined
                      }}>
                        {hasEmoji && optTextAlign === 'center' && (
                          <span style={{ display: 'inline-block', fontSize: `${2 * sizeScale * emojiScale}rem`, lineHeight: `${2.25 * sizeScale * emojiScale}rem` }}>{option.emoji}</span>
                        )}
                        {hasEmoji && optTextAlign === 'center' && checkNode}
                        <span
                          style={{ fontSize: scaledOptionFont, fontFamily: config.fontFamily, fontWeight: fontWeightValue, color: labelColor }}
                          dangerouslySetInnerHTML={{ __html: interpolate(option.text || '') }}
                        />
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div >
        );

      case ElementType.Price:
        const layoutType = (element as any).layout || 'horizontal';
        const borderSizePrice = (element as any).border || 'small';
        const shadowSizePrice = (element as any).shadow || 'none';
        const borderClassPrice = borderSizePrice === 'none' ? 'border-0' : borderSizePrice === 'small' ? 'border' : borderSizePrice === 'medium' ? 'border-2' : 'border-4';
        const shadowClassPrice = shadowSizePrice === 'none' ? '' : shadowSizePrice === 'small' ? 'shadow-sm' : shadowSizePrice === 'medium' ? 'shadow' : 'shadow-lg';
        const getVariant = (color: string | undefined) => {
          const c = color || 'neutral';
          if (c === 'info') return { container: 'bg-blue-50 border-blue-200', title: 'text-blue-700', badgeBg: 'bg-blue-100', badgeText: 'text-blue-800' };
          if (c === 'warning') return { container: 'bg-yellow-50 border-yellow-200', title: 'text-yellow-700', badgeBg: 'bg-yellow-100', badgeText: 'text-yellow-800' };
          if (c === 'success') return { container: 'bg-green-50 border-green-200', title: 'text-green-700', badgeBg: 'bg-green-100', badgeText: 'text-green-800' };
          if (c === 'error') return { container: 'bg-red-50 border-red-200', title: 'text-red-700', badgeBg: 'bg-red-100', badgeText: 'text-red-800' };
          if (c === 'neutral') return { container: 'bg-slate-50 border-slate-200', title: 'text-slate-800', badgeBg: 'bg-slate-100', badgeText: 'text-slate-700' };
          if (c === 'transparent') return { container: 'bg-transparent border-slate-300', title: 'text-slate-800', badgeBg: 'bg-slate-100', badgeText: 'text-slate-700' };
          if (c === 'gray') return { container: 'bg-slate-100 border-slate-300', title: 'text-slate-800', badgeBg: 'bg-slate-200', badgeText: 'text-slate-700' };
          return { container: 'bg-white border-slate-300', title: 'text-slate-800', badgeBg: 'bg-slate-100', badgeText: 'text-slate-700' };
        };
        const items: any[] = ((element as any).items || []);
        const isVertical = layoutType === 'vertical';
        const rowGapRem = Math.max(0.6, mbRem * 0.7);
        const colGapRem = Math.max(0.6, mbRem * 0.7);
        const gridCols = `repeat(${items.length || 1}, minmax(0, 1fr))`;
        const verticalAlign = (element as any).verticalAlign || 'bottom';
        const alignItemsValue = verticalAlign === 'top' ? 'start' : verticalAlign === 'center' ? 'center' : 'end';
        if (previewMode && !isDelayVisible) return null;
        return (
          <div
            style={{
              marginBottom: `${mbRem}rem`,
              display: isVertical ? 'grid' : 'flex',
              gridTemplateColumns: isVertical ? gridCols : undefined,
              flexDirection: isVertical ? undefined : 'column',
              gap: isVertical ? `${colGapRem}rem` : `${rowGapRem}rem`,
              alignItems: isVertical ? alignItemsValue : undefined
            }}
          >
            {items.map((it: any, idx: number) => {
              const v = getVariant(it.color);
              const redirect = it.priceType === 'redirect' && it.url;
              const shouldBlockRedirect = isRedirecting && (it.target !== '_blank');
              const Wrapper: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => redirect && onButtonAction ? (
                <a
                  href={it.url}
                  target={it.target || '_blank'}
                  className={`block ${className || ''} ${shouldBlockRedirect ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                  style={shouldBlockRedirect ? { filter: 'saturate(50%)' } : {}}
                  onClick={(e) => {
                    // Bloqueio imediato: verificar se já está redirecionando (só para _self)
                    if (shouldBlockRedirect) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }

                    // Desabilitar link IMEDIATAMENTE no DOM (antes de async) - só para _self
                    if ((it.target || '_blank') === '_self') {
                      const link = e.currentTarget;
                      link.style.opacity = '0.5';
                      link.style.cursor = 'not-allowed';
                      link.style.pointerEvents = 'none';
                    }

                    if (onButtonAction && it.url) {
                      e.preventDefault();
                      // Passar URL para o handler (UTMs serão adicionados lá)
                      const target = it.target || '_blank';
                      onButtonAction('redirect', it.url, { target });
                    }
                  }}
                >
                  {children}
                </a>
              ) : (
                <div className={className}>{children}</div>
              );

              const sidePad = 'p-4';
              return (
                <Wrapper key={`${element.id}-price-${idx}`}>
                  <div className={`${sidePad} ${borderClassPrice} ${shadowClassPrice} ${v.container} flex flex-col relative`} style={{ borderRadius: globalRadius }}>
                    {isRedirecting && redirect && (it.target || '_blank') === '_self' && (
                      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg z-10" style={{ borderRadius: globalRadius }}>
                        <svg className="animate-spin h-6 w-6 text-slate-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    {it.topHighlight && (
                      <div className="-mt-4 -mx-4 mb-3 px-3 py-1 bg-slate-900 text-white text-sm font-semibold rounded-t-lg text-center" style={{ borderTopLeftRadius: globalRadius, borderTopRightRadius: globalRadius, fontSize: `calc(${config.fontSizes.subtitle} * 1.05)` }}>
                        {interpolate(it.topHighlight)}
                      </div>
                    )}
                    <div className={layoutType === 'horizontal' ? 'flex items-center justify-between' : 'flex flex-col gap-3'}>
                      <div className="flex items-center gap-3">
                        {it.enableCheckbox && (
                          <input
                            type="checkbox"
                            checked={priceCheckboxes[it.id] || false}
                            onChange={(e) => onPriceCheckboxChange?.(it.id, e.target.checked)}
                            className="rounded-md border-slate-300"
                            style={{ width: `${1.25 * sizeScale}rem`, height: `${1.25 * sizeScale}rem` }}
                          />
                        )}
                        <div className={`font-bold ${v.title}`} style={{ fontSize: `calc(${config.fontSizes.subtitle} * 1.15)`, fontFamily: config.fontFamily }} dangerouslySetInnerHTML={{ __html: interpolate(it.title || '') }} />
                      </div>
                      <div className={`rounded-lg ${v.badgeBg} ${v.badgeText} px-3 py-2 flex flex-col items-end`} style={{ minWidth: '8rem' }}>
                        {it.prefix && <span className="text-xs">{interpolate(it.prefix)}</span>}
                        {it.value && <span className="text-xl font-black">{interpolate(it.value)}</span>}
                        {it.suffix && <span className="text-xs">{interpolate(it.suffix)}</span>}
                      </div>
                    </div>
                    {it.bottomHighlight && (
                      <div className="-mb-4 -mx-4 mt-3 px-3 py-1 bg-slate-900 text-white text-sm font-semibold rounded-b-lg text-center" style={{ borderBottomLeftRadius: globalRadius, borderBottomRightRadius: globalRadius, fontSize: `calc(${config.fontSizes.subtitle} * 1.05)` }}>
                        {interpolate(it.bottomHighlight)}
                      </div>
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        );

      case ElementType.Level:
        const lvTitle = (element as any).title || '';
        const lvSubtitle = (element as any).subtitle || '';
        const lvPercent = Math.max(0, Math.min(100, Number((element as any).percent ?? 0)));
        const lvIndicator = (element as any).indicatorText || '';
        const lvLeft = (element as any).legendLeft || '';
        const lvCenter = (element as any).legendCenter || '';
        const lvRight = (element as any).legendRight || '';
        const lvShowMeter = (element as any).showMeter !== false;
        const lvShowProgress = (element as any).showProgress !== false;
        const lvStyleType = (element as any).styleType || 'dashes';
        const lvColorTheme = (element as any).colorTheme || 'theme';
        const lvSegments = 5;
        const lvBorderSize = (element as any).border || 'none';
        const lvShadowSize = (element as any).shadow || 'none';
        const lvBgCandidate = (element as any).backgroundColor;
        const lvBgColor = (typeof lvBgCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(lvBgCandidate)) ? lvBgCandidate : (config.colors.background || '#ffffff');
        const lvBorderClass = lvBorderSize === 'none' ? 'border-0' : lvBorderSize === 'small' ? 'border' : lvBorderSize === 'medium' ? 'border-2' : 'border-4';
        const lvShadowClass = lvShadowSize === 'none' ? '' : lvShadowSize === 'small' ? 'shadow-sm' : lvShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const lvPadRem = lvBorderSize === 'none' ? 0 : lvBorderSize === 'small' ? 0.75 : lvBorderSize === 'medium' ? 1 : 1.25;
        const themeColor = (() => {
          if (lvColorTheme === 'red') return '#ef4444';
          if (lvColorTheme === 'blue') return '#3b82f6';
          if (lvColorTheme === 'green') return '#22c55e';
          if (lvColorTheme === 'yellow') return '#eab308';
          if (lvColorTheme === 'primary') return config.colors.primary;
          return config.colors.primary;
        })();
        const gradient = (() => {
          if (lvColorTheme === 'greenToRed') return 'linear-gradient(90deg, #22c55e, #eab308, #ef4444)';
          if (lvColorTheme === 'redToGreen') return 'linear-gradient(90deg, #ef4444, #eab308, #22c55e)';
          return '';
        })();
        const lvBarColorCandidate = (element as any).barColor;
        const hasCustomBarColor = typeof lvBarColorCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(lvBarColorCandidate);
        const lvBarColor = hasCustomBarColor ? lvBarColorCandidate : config.colors.primary;
        // Determinar a cor final da barra: gradiente > cor personalizada > cor do tema
        const finalBarColor = gradient || (hasCustomBarColor ? lvBarColor : themeColor);
        const barBg = '#e5e7eb';
        const lvTitleColor = (element as any).titleColor || (config.colors.title || config.colors.text || '#0f172a');
        const lvTextColor = (element as any).textColor || (config.colors.text || '#0f172a');

        // Estado para animação da barra
        const [lvAnimatedPercent, setLvAnimatedPercent] = React.useState(0);
        React.useEffect(() => {
          // Reset ao 0 quando o elemento muda
          setLvAnimatedPercent(0);

          // Animação suave usando requestAnimationFrame
          const duration = 1500; // ms
          const startTime = Date.now();
          const startValue = 0;
          const endValue = lvPercent;

          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out function
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentValue = startValue + (endValue - startValue) * easeOut;

            setLvAnimatedPercent(currentValue);

            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };

          // Pequeno delay antes de iniciar a animação
          const timer = setTimeout(() => {
            requestAnimationFrame(animate);
          }, 100);

          return () => clearTimeout(timer);
        }, [previewMode, element.id, lvPercent]);

        if (previewMode && !isDelayVisible) return null;
        return (
          <div className={`${lvBorderClass} border-slate-300 ${lvShadowClass}`} style={{ borderRadius: globalRadius, marginBottom: `${mbRem}rem`, backgroundColor: lvBgColor, padding: `${lvPadRem}rem` }}>
            {(lvTitle || lvSubtitle) && (
              <div className="flex items-start justify-between mb-2">
                <div>
                  {lvTitle && (
                    <div className="font-semibold" style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 1.15})`, fontFamily: config.fontFamily, color: lvTitleColor }}>
                      {interpolate(lvTitle)}
                    </div>
                  )}
                  {lvSubtitle && (
                    <div style={{ fontSize: config.fontSizes.subtitle, fontFamily: config.fontFamily, color: lvTextColor }}>
                      {interpolate(lvSubtitle)}
                    </div>
                  )}
                </div>
                {lvShowProgress && (
                  <div style={{ fontSize: `calc(${config.fontSizes.subtitle} * 0.95)`, fontFamily: config.fontFamily, color: lvTextColor }}>{Math.round(lvAnimatedPercent)}%</div>
                )}
              </div>
            )}
            <div className="w-full" style={{ paddingTop: '0.25rem' }}>
              {lvStyleType === 'line' ? (
                <div className="relative w-full rounded-full" style={{ backgroundColor: barBg, height: '0.6rem' }}>
                  <div className="rounded-full" style={{ width: `${lvAnimatedPercent}%`, background: finalBarColor, height: '0.6rem' }} />
                  {lvShowMeter && (
                    <div style={{ position: 'absolute', left: `${lvAnimatedPercent}%`, top: '50%', transform: 'translate(-50%, -50%)' }}>
                      {lvIndicator && (
                        <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translate(-50%, -4px)' }}>
                          <span className="px-2 py-1 text-xs bg-slate-800 text-white rounded shadow" style={{ fontFamily: config.fontFamily, whiteSpace: 'nowrap' }}>{lvIndicator}</span>
                          <svg width="10" height="6" viewBox="0 0 10 6" className="text-slate-800" style={{ display: 'block', margin: '0 auto' }}>
                            <path d="M0 0 L5 6 L10 0 Z" fill="currentColor" />
                          </svg>
                        </div>
                      )}
                      <div className="w-6 h-6 rounded-full border-2" style={{ borderColor: '#0f172a', backgroundColor: '#ffffff' }} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative w-full">
                  {/* Camada de fundo com gradiente completo (se houver gradiente) */}
                  {gradient && (
                    <div className="absolute inset-0 flex items-center gap-1" style={{ pointerEvents: 'none' }}>
                      {Array.from({ length: lvSegments }).map((_, i) => (
                        <div key={`${element.id}-seg-bg-${i}`} className="flex-1 rounded-full" style={{ height: '0.6rem', background: 'transparent' }} />
                      ))}
                    </div>
                  )}

                  {/* Barra com segmentos */}
                  <div className="relative w-full flex items-center gap-1">
                    {Array.from({ length: lvSegments }).map((_, i) => {
                      const start = i / lvSegments;
                      const end = (i + 1) / lvSegments;
                      const ratio = lvAnimatedPercent / 100;
                      const segFill = Math.max(0, Math.min(1, (ratio - start) / (end - start)));
                      const segFillPct = Math.round(segFill * 100);

                      // Se há gradiente, calcular a cor específica deste segmento baseado na posição
                      let segmentColor = finalBarColor;
                      if (gradient) {
                        // Posição do centro do segmento na barra completa (0 a 1)
                        const segmentCenter = (start + end) / 2;

                        if (lvColorTheme === 'greenToRed') {
                          // Verde (0) -> Amarelo (0.5) -> Vermelho (1)
                          if (segmentCenter < 0.5) {
                            // Verde para Amarelo
                            const t = segmentCenter * 2; // 0 a 1
                            segmentColor = `rgb(${Math.round(34 + (234 - 34) * t)}, ${Math.round(197 + (179 - 197) * t)}, ${Math.round(94 + (8 - 94) * t)})`;
                          } else {
                            // Amarelo para Vermelho
                            const t = (segmentCenter - 0.5) * 2; // 0 a 1
                            segmentColor = `rgb(${Math.round(234 + (239 - 234) * t)}, ${Math.round(179 + (68 - 179) * t)}, ${Math.round(8 + (68 - 8) * t)})`;
                          }
                        } else if (lvColorTheme === 'redToGreen') {
                          // Vermelho (0) -> Amarelo (0.5) -> Verde (1)
                          if (segmentCenter < 0.5) {
                            // Vermelho para Amarelo
                            const t = segmentCenter * 2; // 0 a 1
                            segmentColor = `rgb(${Math.round(239 + (234 - 239) * t)}, ${Math.round(68 + (179 - 68) * t)}, ${Math.round(68 + (8 - 68) * t)})`;
                          } else {
                            // Amarelo para Verde
                            const t = (segmentCenter - 0.5) * 2; // 0 a 1
                            segmentColor = `rgb(${Math.round(234 + (34 - 234) * t)}, ${Math.round(179 + (197 - 179) * t)}, ${Math.round(8 + (94 - 8) * t)})`;
                          }
                        }
                      }

                      return (
                        <div key={`${element.id}-seg-${i}`} className="flex-1 rounded-full" style={{ backgroundColor: barBg, height: '0.6rem' }}>
                          <div className="rounded-full" style={{ width: `${segFillPct}%`, background: segmentColor, height: '0.6rem' }} />
                        </div>
                      );
                    })}
                    {lvShowMeter && (
                      <div style={{ position: 'absolute', left: `${lvAnimatedPercent}%`, top: '50%', transform: 'translate(-50%, -50%)' }}>
                        {lvIndicator && (
                          <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translate(-50%, -4px)' }}>
                            <span className="px-2 py-1 text-xs bg-slate-800 text-white rounded shadow" style={{ fontFamily: config.fontFamily, whiteSpace: 'nowrap' }}>{lvIndicator}</span>
                            <svg width="10" height="6" viewBox="0 0 10 6" className="text-slate-800" style={{ display: 'block', margin: '0 auto' }}>
                              <path d="M0 0 L5 6 L10 0 Z" fill="currentColor" />
                            </svg>
                          </div>
                        )}
                        <div className="w-6 h-6 rounded-full border-2" style={{ borderColor: '#0f172a', backgroundColor: '#ffffff' }} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-2 relative" style={{ fontFamily: config.fontFamily, fontSize: config.fontSizes.subtitle, height: '1.2rem', color: lvTextColor }}>
              <span style={{ position: 'absolute', left: 0 }}>{interpolate(lvLeft)}</span>
              <span style={{ position: 'absolute', left: lvStyleType === 'dashes' ? 'calc(50% + 0.125rem)' : '50%', transform: 'translateX(-50%)' }}>{interpolate(lvCenter)}</span>
              <span style={{ position: 'absolute', right: 0 }}>{interpolate(lvRight)}</span>
            </div>
          </div>
        );

      case ElementType.List:
        const listLayout = (element as any).layout || 'list';
        const listTextAlign = (element as any).textAlign || 'left';
        const listBorderSize = (element as any).border || 'medium';
        const listShadowSize = (element as any).shadow || 'none';
        const listSpacingSize = (element as any).spacing || 'medium';
        const listTitlePos = (element as any).titlePosition || 'top';
        const listTitleScale = sizeScale * 1.15;
        const listContainerClass =
          listLayout === 'list'
            ? 'grid grid-cols-1 gap-2'
            : listLayout === 'grid2'
              ? 'grid grid-cols-2 gap-2'
              : listLayout === 'grid3'
                ? 'grid grid-cols-3 gap-2'
                : 'grid grid-cols-4 gap-2';
        const listPadClass = listSpacingSize === 'none'
          ? 'p-1'
          : listSpacingSize === 'simple'
            ? 'p-2'
            : listSpacingSize === 'medium'
              ? 'p-3'
              : listSpacingSize === 'large'
                ? 'p-4'
                : 'p-6';
        const listBorderClass = listBorderSize === 'none' ? 'border-0' : listBorderSize === 'small' ? 'border' : listBorderSize === 'medium' ? 'border-2' : 'border-4';
        const listShadowClass = listShadowSize === 'none' ? '' : listShadowSize === 'small' ? 'shadow-sm' : listShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const listItems: { title: string; description: string; bgColor?: string; textColor?: string }[] = (element as any).items || [];
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className={listContainerClass}>
              {listItems.map((item, idx) => {
                const titleHtml = (item.title || '').trim();
                const descHtml = (item.description || '').trim();
                const hasTitle = titleHtml.length > 0;
                const hasDesc = descHtml.length > 0;
                return (
                  <div
                    key={idx}
                    className={`${listPadClass} ${listBorderClass} border-slate-300 ${listShadowClass}`}
                    style={{ borderRadius: globalRadius, textAlign: listTextAlign, backgroundColor: item.bgColor || '#ffffff' }}
                  >
                    {listTitlePos === 'top' && hasTitle && (
                      <div
                        className="font-semibold"
                        style={{ fontSize: `calc(${config.fontSizes.option} * ${listTitleScale})`, fontFamily: config.fontFamily, color: item.textColor || (config.colors.text || '#0f172a'), marginBottom: hasDesc ? '0.75rem' : 0 }}
                        dangerouslySetInnerHTML={{ __html: interpolate(titleHtml) }}
                      />
                    )}
                    {hasDesc && (
                      <div
                        className="text-slate-700"
                        style={{ fontSize: config.fontSizes.subtitle, fontFamily: config.fontFamily, whiteSpace: 'pre-line', color: item.textColor || (config.colors.text || '#64748b') }}
                        dangerouslySetInnerHTML={{ __html: interpolate(descHtml) }}
                      />
                    )}
                    {listTitlePos === 'bottom' && hasTitle && (
                      <div
                        className="font-semibold"
                        style={{ fontSize: `calc(${config.fontSizes.option} * ${listTitleScale})`, fontFamily: config.fontFamily, color: item.textColor || (config.colors.text || '#0f172a'), marginTop: hasDesc ? '1rem' : 0 }}
                        dangerouslySetInnerHTML={{ __html: interpolate(titleHtml) }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case ElementType.Chart:
        const chartLayout = (element as any).layout || 'grid2';
        const chartBorderSize = (element as any).border || 'small';
        const chartShadowSize = (element as any).shadow || 'none';
        const chartAnim = (element as any).animation || 'none';
        const chartBg = (element as any).backgroundColor || '#ffffff';
        const chartBorderColor = (element as any).borderColor || (config.colors.primary || '#3b82f6');
        const chartTextColor = (element as any).textColor || (config.colors.text || '#64748b');
        React.useEffect(() => {
          const id = 'opt-extras-style';
          if (document.getElementById(id)) return;
          const el = document.createElement('style');
          el.id = id;
          el.textContent = `
            .opt-emboss::before { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: var(--opt-emboss-h, 0.5rem); background: rgba(var(--opt-color-dark-rgb, 0,0,0), 0.4); border-bottom-left-radius: inherit; border-bottom-right-radius: inherit; pointer-events: none; }
            .opt-ring:hover { box-shadow: 0 0 0 2px var(--opt-ring-color, #3b82f6); }
            .opt-ring-selected { box-shadow: 0 0 0 2px var(--opt-ring-color, #3b82f6); }
            .opt-border:hover { border-color: var(--opt-border-color, #3b82f6); }
            .opt-border-selected { border-color: var(--opt-border-color, #3b82f6); }
          `;
          document.head.appendChild(el);
        }, []);
        const chartContainerClass =
          chartLayout === 'list'
            ? 'grid grid-cols-1 gap-2'
            : chartLayout === 'grid2'
              ? 'grid grid-cols-2 gap-2'
              : 'grid grid-cols-3 gap-2';
        const chartBorderClass = chartBorderSize === 'none' ? 'border-0' : chartBorderSize === 'small' ? 'border' : chartBorderSize === 'medium' ? 'border-2' : 'border-4';
        const chartShadowClass = chartShadowSize === 'none' ? '' : chartShadowSize === 'small' ? 'shadow-sm' : chartShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const chartItems: { type: 'bar' | 'donut'; title?: string; text?: string; percent?: number; color?: string; percentColor?: string }[] = (element as any).items || [];
        const [animReady, setAnimReady] = React.useState(false);
        React.useEffect(() => {
          setAnimReady(false);
          const t = setTimeout(() => setAnimReady(true), 30);
          return () => clearTimeout(t);
        }, [previewMode, element.id, chartItems.length]);
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className={chartContainerClass}>
              {chartItems.map((item, idx) => {
                const pct = Math.max(0, Math.min(100, Number(item.percent || 0)));
                const showPct = animReady ? pct : 0;
                const color = item.color || (config.colors.primary || '#3b82f6');
                const animClass = chartAnim === 'zoom' ? 'transition-transform duration-200 hover:scale-[1.05]' : chartAnim === 'border' ? 'opt-border' : '';
                const commonStyle: React.CSSProperties = {
                  borderRadius: globalRadius,
                  backgroundColor: chartBg,
                  ['--opt-border-color' as any]: chartBorderColor
                };
                return (
                  <div key={`${element.id}-chart-${idx}`} className={`${chartBorderClass} border-slate-300 ${chartShadowClass} ${animClass}`} style={commonStyle}>
                    <div className={`${item.type === 'donut' ? 'p-3' : 'p-6'} flex flex-col items-center`}>
                      {item.title && (
                        <div className="text-center font-medium mb-4" style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 1.0})`, color: chartTextColor, fontFamily: config.fontFamily }} dangerouslySetInnerHTML={{ __html: interpolate(item.title || '') }} />
                      )}
                      {item.type === 'donut' ? (
                        (() => {
                          const r = 45;
                          const c = 2 * Math.PI * r;
                          const offset = c * (1 - showPct / 100);
                          const pctColor = item.percentColor || '#0f172a';
                          const baseSize = 120;
                          return (
                            <div className="relative" style={{ width: baseSize, maxWidth: '90%', aspectRatio: '1 / 1' }}>
                              <svg viewBox="0 0 100 100" className="block w-full h-full">
                                <circle cx={50} cy={50} r={r} stroke="#e5e7eb" strokeWidth={10} fill="none" />
                                <circle
                                  cx={50}
                                  cy={50}
                                  r={r}
                                  stroke={color}
                                  strokeWidth={10}
                                  fill="none"
                                  strokeLinecap="round"
                                  style={{
                                    strokeDasharray: c,
                                    strokeDashoffset: offset,
                                    transform: 'rotate(-90deg)',
                                    transformOrigin: '50% 50%',
                                    transition: 'stroke-dashoffset 800ms ease-out'
                                  }}
                                />
                              </svg>
                              <div className="absolute inset-0 grid place-items-center" style={{ zIndex: 2 }}>
                                <span className="font-semibold" style={{ color: pctColor, fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 1.02})` }}>{`${pct}%`}</span>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        (() => {
                          const pctColor = item.percentColor || '#0f172a';
                          return (
                            <div className="w-16 h-28 rounded-lg overflow-hidden bg-slate-100 relative">
                              <div className="absolute left-1 right-1 top-1 font-semibold text-center" style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 0.95})`, color: pctColor, zIndex: 2 }}>{`${pct}%`}</div>
                              <div className="absolute left-0 right-0 bottom-0" style={{ height: `${showPct}%`, transition: 'height 800ms ease-out' }}>
                                <div className="mx-2 rounded-b-md" style={{ backgroundColor: color, height: '100%' }} />
                              </div>
                            </div>
                          );
                        })()
                      )}
                      {item.text && (
                        <div className="mt-4 text-center" style={{ color: chartTextColor, fontFamily: config.fontFamily }}>
                          <div className="text-slate-700" style={{ fontSize: config.fontSizes.subtitle, color: chartTextColor }} dangerouslySetInnerHTML={{ __html: interpolate(item.text || '') }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case ElementType.Report:
        const reportBorderSize = (element as any).border || 'small';
        const reportShadowSize = (element as any).shadow || 'none';
        const reportBg = (element as any).backgroundColor || '#ffffff';
        const reportBorderColorCandidate = (element as any).borderColor;
        const reportTitleColor = (element as any).titleColor || (config.colors.text || '#0f172a');
        const scoreColor = (element as any).scoreColor || (config.colors.primary || '#3b82f6');
        const percentColor = (element as any).percentColor || '#0f172a';
        const reportBorderClass = reportBorderSize === 'none' ? 'border-0' : reportBorderSize === 'small' ? 'border' : reportBorderSize === 'medium' ? 'border-2' : 'border-4';
        const reportShadowClass = reportShadowSize === 'none' ? '' : reportShadowSize === 'small' ? 'shadow-sm' : reportShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const title = (element as any).title || 'Sua Pontuação';
        const resultLabel = (element as any).resultLabel || '';
        const subtitle = '';
        const score = Math.max(0, Math.min(100, Number((element as any).score || 0)));
        const [animReadyReport, setAnimReadyReport] = React.useState(false);
        React.useEffect(() => {
          setAnimReadyReport(false);
          const t = setTimeout(() => setAnimReadyReport(true), 30);
          return () => clearTimeout(t);
        }, [previewMode, element.id, score]);
        const showPctReport = animReadyReport ? score : 0;
        React.useEffect(() => {
          const id = 'report-result-anim-style';
          if (document.getElementById(id)) return;
          const el = document.createElement('style');
          el.id = id;
          el.textContent = `@keyframes report-fade-in{0%{opacity:0}100%{opacity:1}}@keyframes report-pulse-zoom{0%{transform:scale(1)}50%{transform:scale(1.08)}100%{transform:scale(1)}}`;
          document.head.appendChild(el);
        }, []);
        const [imgReady, setImgReady] = React.useState(false);
        React.useEffect(() => {
          setImgReady(false);
        }, [previewMode, element.id, (element as any).imageUrl]);
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className={`${reportBorderClass} border-slate-300 ${reportShadowClass}`} style={{ borderRadius: globalRadius, backgroundColor: reportBg, borderColor: (typeof reportBorderColorCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(reportBorderColorCandidate)) ? reportBorderColorCandidate : undefined }}>
                <div className="p-6 flex flex-col items-center justify-center" style={{ minHeight: '12rem', height: '100%' }}>
                  <div className="font-semibold mb-4 text-center" style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 1.15})`, color: reportTitleColor, fontFamily: config.fontFamily }} dangerouslySetInnerHTML={{ __html: interpolate(title) }} />
                  {(() => {
                    const r = 45;
                    const c = 2 * Math.PI * r;
                    const offset = c * (1 - showPctReport / 100);
                    const baseSize = 160;
                    return (
                      <div className="relative" style={{ width: baseSize, maxWidth: '90%', aspectRatio: '1 / 1' }}>
                        <svg viewBox="0 0 100 100" className="block w-full h-full">
                          <circle cx={50} cy={50} r={r} stroke="#e5e7eb" strokeWidth={10} fill="none" />
                          <circle cx={50} cy={50} r={r} stroke={scoreColor} strokeWidth={10} fill="none" strokeLinecap="round" style={{ strokeDasharray: c, strokeDashoffset: offset, transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 800ms ease-out' }} />
                        </svg>
                        <div className="absolute inset-0 grid place-items-center">
                          <span className="font-semibold" style={{ color: percentColor, fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 1.2})` }}>{`${score}%`}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {resultLabel && (
                  <div className={`${reportBorderClass} border-slate-300 ${reportShadowClass}`} style={{ borderRadius: globalRadius, backgroundColor: reportBg, borderColor: (typeof reportBorderColorCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(reportBorderColorCandidate)) ? reportBorderColorCandidate : undefined }}>
                    <div className="px-4 py-3 text-center">
                      <span className="inline-block" style={{ fontSize: config.fontSizes.subtitle, color: reportTitleColor, fontFamily: config.fontFamily, animation: 'report-fade-in 400ms ease-out forwards, report-pulse-zoom 240ms ease-in-out 3 420ms, report-pulse-zoom 240ms ease-in-out 3 3140ms' }} dangerouslySetInnerHTML={{ __html: interpolate(resultLabel) }} />
                    </div>
                  </div>
                )}
                <div className={`${reportBorderClass} border-slate-300 ${reportShadowClass}`} style={{ borderRadius: globalRadius, backgroundColor: reportBg, borderColor: (typeof reportBorderColorCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(reportBorderColorCandidate)) ? reportBorderColorCandidate : undefined }}>
                  <div className="flex items-end justify-center" style={{ minHeight: '12rem', height: '100%' }}>
                    {((element as any).imageUrl) ? (
                      <img src={(element as any).imageUrl} alt="" onLoad={() => setImgReady(true)} className={`w-full h-full border border-slate-300 object-cover object-bottom transition-opacity duration-700 ${imgReady ? 'opacity-100' : 'opacity-0'}`} style={{ borderRadius: globalRadius, borderColor: (typeof reportBorderColorCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(reportBorderColorCandidate)) ? reportBorderColorCandidate : undefined }} />
                    ) : (
                      <div className="w-full border border-slate-300 flex items-end justify-center" style={{ borderRadius: globalRadius, backgroundColor: '#f1f5f9', minHeight: '12rem', borderColor: (typeof reportBorderColorCandidate === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(reportBorderColorCandidate)) ? reportBorderColorCandidate : undefined }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.5rem' }}>
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15l-5-5-4 4-2-2-6 6" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case ElementType.Seal:
        const sealIconName = (element as any).icon || 'medal';
        const SealIcon = SEAL_ICONS[sealIconName as keyof typeof SEAL_ICONS] || SEAL_ICONS['medal'];
        const sealAlign = (element as any).align || 'center';
        const sealTextAlign = sealAlign;
        const sealItemsClass = sealAlign === 'left' ? 'items-start' : sealAlign === 'right' ? 'items-end' : 'items-center';
        const sealIconSize = (element as any).iconSize || 'medium';
        const iconSizeClass = sealIconSize === 'small' ? 'w-8 h-8' : sealIconSize === 'large' ? 'w-12 h-12' : 'w-10 h-10';
        const sealIconColor = (element as any).iconColor || (config.colors.title || '#0f172a');
        const sealBg = (element as any).backgroundColor || '#ffffff';
        const sealTitleColor = (element as any).titleColor || (config.colors.title || '#0f172a');
        const sealTextColor = (element as any).textColor || (config.colors.text || '#64748b');
        const sealBorderSize = (element as any).border || 'small';
        const sealShadowSize = (element as any).shadow || 'none';
        const sealBorderClass = sealBorderSize === 'none' ? 'border-0' : sealBorderSize === 'small' ? 'border' : sealBorderSize === 'medium' ? 'border-2' : 'border-4';
        const sealShadowClass = sealShadowSize === 'none' ? '' : sealShadowSize === 'small' ? 'shadow-sm' : sealShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const sealTitleScale = sizeScale * 1.15; // igual ao título de Lista
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className={`${sealBorderClass} border-slate-300 ${sealShadowClass}`} style={{ borderRadius: globalRadius, textAlign: sealTextAlign, backgroundColor: sealBg }}>
              <div className={`p-6 flex flex-col ${sealItemsClass} justify-center`}>
                <SealIcon className={`${iconSizeClass}`} style={{ color: sealIconColor }} />
                {(element as any).title && (
                  <div
                    className="mt-3 font-semibold text-slate-900"
                    style={{ fontSize: `calc(${config.fontSizes.option} * ${sealTitleScale})`, fontFamily: config.fontFamily, color: sealTitleColor }}
                    dangerouslySetInnerHTML={{ __html: interpolate((element as any).title || '') }}
                  />
                )}
                {(element as any).description && (
                  <div
                    className="mt-2 text-slate-700"
                    style={{ fontSize: config.fontSizes.subtitle, fontFamily: config.fontFamily, whiteSpace: 'pre-line', color: sealTextColor }}
                    dangerouslySetInnerHTML={{ __html: interpolate((element as any).description || '') }}
                  />
                )}
              </div>
            </div>
          </div>
        );

      case ElementType.Image:
        const imgWidth = (element as any).widthPercent ?? 100;
        const imgAlign = (element as any).align || 'center';
        const imgJustify = imgAlign === 'left' ? 'flex-start' : imgAlign === 'right' ? 'flex-end' : 'center';
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className="relative w-full max-w-2xl mx-auto" style={{ display: 'flex', justifyContent: imgJustify, flexDirection: 'column', alignItems: imgJustify === 'center' ? 'center' : imgJustify === 'flex-start' ? 'flex-start' : 'flex-end' }}>
              {element.url ? (
                <>
                  {!imageLoaded && (
                    <div
                      className="bg-slate-100 rounded-lg animate-pulse flex items-center justify-center border border-slate-200"
                      style={{
                        width: `${imgWidth}%`,
                        height: '200px', // Altura fixa para o esqueleto
                        borderRadius: globalRadius
                      }}
                    >
                      <svg className="w-10 h-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <img
                    ref={imgRef}
                    src={element.url}
                    alt={element.alt || ''}
                    onLoad={() => setImageLoaded(true)}
                    loading="eager"
                    onClick={() => {
                      if (previewMode && (element as any).clickToNext && onNext) {
                        onNext();
                      }
                    }}
                    className={`border border-slate-300 transition-opacity duration-300 ${imageLoaded ? 'opacity-100 relative' : 'opacity-0 absolute top-0 left-0 -z-10'} ${(element as any).clickToNext && previewMode ? 'cursor-pointer hover:opacity-90' : ''}`}
                    style={{
                      borderRadius: globalRadius,
                      width: `${imgWidth}%`,
                      height: 'auto',
                      backgroundColor: '#f1f5f9'
                    }}
                  />
                </>
              ) : (
                <div
                  className="w-full aspect-video border border-slate-300 flex items-center justify-center"
                  style={{ borderRadius: globalRadius, backgroundColor: '#f1f5f9' }}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5-4 4-2-2-6 6" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        );

      case ElementType.BeforeAfter:
        const baWidth = (element as any).widthPercent ?? 100;
        const baAlign = (element as any).align || 'center';
        const baJustify = baAlign === 'left' ? 'flex-start' : baAlign === 'right' ? 'flex-end' : 'center';
        const baOrientation = (element as any).orientation || 'vertical';
        const baStart = Math.max(0, Math.min(100, (element as any).startPercent ?? 50));
        const [baPos, setBaPos] = React.useState(baStart);
        React.useEffect(() => { setBaPos(baStart); }, [element.id, baStart]);
        const baBorder = (element as any).border || 'small';
        const baShadow = (element as any).shadow || 'none';
        const baBorderClass = baBorder === 'none' ? 'border-0' : baBorder === 'small' ? 'border' : baBorder === 'medium' ? 'border-2' : 'border-4';
        const baShadowClass = baShadow === 'none' ? '' : baShadow === 'small' ? 'shadow-sm' : baShadow === 'medium' ? 'shadow' : 'shadow-lg';
        const baContainerRef = React.useRef<HTMLDivElement | null>(null);
        React.useEffect(() => {
          const el = baContainerRef.current;
          if (!el) return;
          if (!previewMode) return;
          const move = (e: PointerEvent) => {
            const rect = el.getBoundingClientRect();
            if (baOrientation === 'vertical') {
              const x = e.clientX - rect.left;
              setBaPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
            } else {
              const y = e.clientY - rect.top;
              setBaPos(Math.max(0, Math.min(100, (y / rect.height) * 100)));
            }
          };
          let active = false;
          const onDown = (e: PointerEvent) => { active = true; move(e); };
          const onMove = (e: PointerEvent) => { if (!active) return; move(e); };
          const onUp = () => { active = false; };
          el.addEventListener('pointerdown', onDown);
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          return () => {
            el.removeEventListener('pointerdown', onDown);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
          };
        }, [previewMode, baOrientation]);
        const beforeLabel = (element as any).beforeLabel || '';
        const afterLabel = (element as any).afterLabel || '';
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className="w-full max-w-2xl mx-auto" style={{ display: 'flex', justifyContent: baJustify }}>
              <div
                ref={baContainerRef}
                className={`${baBorderClass} border-slate-200 ${baShadowClass} relative overflow-hidden bg-slate-100`}
                style={{ borderRadius: globalRadius, width: `${baWidth}%` }}
              >
                {(element as any).afterUrl ? (
                  <img src={(element as any).afterUrl} alt="" className="w-full h-full object-cover object-center select-none pointer-events-none" />
                ) : (
                  <div className="w-full aspect-video" />
                )}
                <div
                  className="absolute inset-0"
                  style={{
                    clipPath: baOrientation === 'vertical'
                      ? `inset(0 ${Math.max(0, 100 - baPos)}% 0 0)`
                      : `inset(${Math.max(0, 100 - baPos)}% 0 0 0)`
                  }}
                >
                  {(element as any).beforeUrl ? (
                    <img src={(element as any).beforeUrl} alt="" className="w-full h-full object-cover object-center select-none pointer-events-none" />
                  ) : null}
                </div>
                {baOrientation === 'vertical' ? (
                  <div className="absolute top-0 bottom-0" style={{ left: `calc(${baPos}% - 1px)` }}>
                    <div className="w-[2px] h-full bg-white/70 border-l border-r border-slate-300" />
                  </div>
                ) : (
                  <div className="absolute left-0 right-0" style={{ top: `calc(${baPos}% - 1px)` }}>
                    <div className="h-[2px] w-full bg-white/70 border-t border-b border-slate-300" />
                  </div>
                )}
                {beforeLabel ? (<div className="absolute top-2 left-2 px-2 py-1 text-xs rounded bg-white/80 text-slate-700 border border-slate-300">{interpolate(beforeLabel)}</div>) : null}
                {afterLabel ? (<div className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-white/80 text-slate-700 border border-slate-300">{interpolate(afterLabel)}</div>) : null}
                <div
                  className="absolute"
                  style={{ left: baOrientation === 'vertical' ? `calc(${baPos}% - 24px)` : 'calc(50% - 24px)', top: baOrientation === 'horizontal' ? `calc(${baPos}% - 24px)` : 'calc(50% - 24px)' }}
                >
                  <div className={`w-12 h-12 rounded-full bg-white border-2 border-slate-300 shadow-lg flex items-center justify-center ${previewMode ? (baOrientation === 'vertical' ? 'cursor-col-resize' : 'cursor-row-resize') : ''}`}>
                    {baOrientation === 'vertical' ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-700">
                        <path d="M10 12l-4 4M10 12l-4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                        <path d="M14 12l4 4M14 12l4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-700">
                        <path d="M12 10l4-4M12 10l-4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                        <path d="M12 14l4 4M12 14l-4 4" stroke="currentColor" strokeWidth="2" fill="none" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case ElementType.Carousel:
        const cWidth = (element as any).widthPercent ?? 100;
        const cAlign = (element as any).align || 'center';
        const cJustify = cAlign === 'left' ? 'flex-start' : cAlign === 'right' ? 'flex-end' : 'center';
        const cItems: { imageUrl?: string; alt?: string; description?: string }[] = (element as any).items || [];
        const cLayout = (element as any).layout || 'image-text';
        const cPagination = (element as any).pagination !== false;
        const cAutoplay = (element as any).autoplay || false;
        const cDelay = (element as any).autoplayDelayMs || 3000;
        const cRatio = (element as any).ratio || 'auto';
        const [idx, setIdx] = React.useState(0);
        const [animStage, setAnimStage] = React.useState(0);
        const containerRef = React.useRef<HTMLDivElement | null>(null);
        const viewportRef = React.useRef<HTMLDivElement | null>(null);

        const dragState = React.useRef<{ active: boolean; startX: number; moved: boolean }>({ active: false, startX: 0, moved: false });
        React.useEffect(() => {
          setIdx(0);
        }, [element.id]);

        React.useEffect(() => {
          if (!previewMode) return;
          if (!cAutoplay) return;
          if (cItems.length <= 1) return;
          const t = setInterval(() => {
            setIdx((prev) => (prev + 1) % cItems.length);
          }, Math.max(800, cDelay));
          return () => clearInterval(t);
        }, [previewMode, cAutoplay, cItems.length, cDelay]);
        React.useEffect(() => {
          setAnimStage(0);
          const t = setTimeout(() => setAnimStage(1), 20);
          return () => clearTimeout(t);
        }, [idx]);

        React.useEffect(() => {
          const el = containerRef.current;
          if (!el) return;
          if (!previewMode) return;
          const onDown = (e: PointerEvent) => {
            dragState.current.active = true;
            dragState.current.startX = e.clientX;
            dragState.current.moved = false;
          };
          const onMove = (e: PointerEvent) => {
            if (!dragState.current.active) return;
            const dx = e.clientX - dragState.current.startX;
            if (Math.abs(dx) > 40) {
              dragState.current.moved = true;
              dragState.current.active = false;
              if (dx < 0) {
                setIdx((prev) => (prev + 1) % Math.max(cItems.length, 1));
              } else {
                setIdx((prev) => (prev - 1 + Math.max(cItems.length, 1)) % Math.max(cItems.length, 1));
              }
            }
          };
          const onUp = () => {
            dragState.current.active = false;
          };
          el.addEventListener('pointerdown', onDown);
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          return () => {
            el.removeEventListener('pointerdown', onDown);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
          };
        }, [previewMode, cItems.length]);
        const cur = cItems[Math.max(0, Math.min(idx, cItems.length - 1))] || {};
        const carouselRatioMap: Record<string, string> = { '1:1': '1 / 1', '16:9': '16 / 9', '4:3': '4 / 3', '9:16': '9 / 16' };
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div ref={containerRef} className="w-full max-w-2xl mx-auto" style={{ display: 'flex', justifyContent: cJustify }}>
              <div style={{ width: `${cWidth}%` }}>
                <div className="bg-transparent" style={{ borderRadius: globalRadius }}>
                  <div ref={viewportRef} className="relative w-full overflow-hidden" style={{ borderTopLeftRadius: globalRadius, borderTopRightRadius: globalRadius }}>
                    {cRatio !== 'auto' ? (
                      <div className="w-full overflow-hidden" style={{ aspectRatio: carouselRatioMap[cRatio] || '16 / 9', position: 'relative', backgroundColor: '#e2e8f0' }}>
                        {cur.imageUrl ? (
                          <img src={cur.imageUrl} alt={cur.alt || ''} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block', zIndex: 2 }} />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5-4 4-2-2-6 6" />
                            </svg>
                          </div>
                        )}
                        {cItems.length > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => setIdx(prev => (prev - 1 + Math.max(cItems.length, 1)) % Math.max(cItems.length, 1))}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white border border-slate-300 flex items-center justify-center shadow-sm"
                              style={{ pointerEvents: previewMode ? 'auto' : 'none' }}
                              aria-label="Anterior"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M15 6l-6 6 6 6" /></svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setIdx(prev => (prev + 1) % Math.max(cItems.length, 1))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white border border-slate-300 flex items-center justify-center shadow-sm"
                              style={{ pointerEvents: previewMode ? 'auto' : 'none' }}
                              aria-label="Próximo"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="w-full overflow-hidden" style={{ position: 'relative', backgroundColor: '#e2e8f0' }}>
                        {cur.imageUrl ? (
                          <img src={cur.imageUrl} alt={cur.alt || ''} style={{ width: '100%', height: 'auto', display: 'block' }} />
                        ) : (
                          <div className="w-full aspect-video flex items-center justify-center">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5-4 4-2-2-6 6" />
                            </svg>
                          </div>
                        )}
                        {cItems.length > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => setIdx(prev => (prev - 1 + Math.max(cItems.length, 1)) % Math.max(cItems.length, 1))}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white border border-slate-300 flex items-center justify-center shadow-sm"
                              style={{ pointerEvents: previewMode ? 'auto' : 'none' }}
                              aria-label="Anterior"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M15 6l-6 6 6 6" /></svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setIdx(prev => (prev + 1) % Math.max(cItems.length, 1))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white border border-slate-300 flex items-center justify-center shadow-sm"
                              style={{ pointerEvents: previewMode ? 'auto' : 'none' }}
                              aria-label="Próximo"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {cLayout === 'image-text' && (
                      <div className="px-4 py-3 text-slate-600" style={{ fontFamily: config.fontFamily, fontSize: config.fontSizes.subtitle }}>
                        <div style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: interpolate(cur.description || '') }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {cPagination && cItems.length > 1 && (
              <div className="pb-3 flex items-center justify-center gap-2" style={{ pointerEvents: previewMode ? 'auto' : 'none' }}>
                {cItems.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    className="rounded-full"
                    style={{ width: 8, height: 8, backgroundColor: i === idx ? '#0f172a' : '#cbd5e1' }}
                    aria-label={`Ir para slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        );

      case ElementType.Audio:
        if (previewMode && !isDelayVisible) return null;
        const audioStyle = (element as any).style || 'default';

        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            {audioStyle === 'whatsapp' ? (
              <div className="flex justify-center w-full">
                <WhatsAppAudioPlayer url={element.url} previewMode={previewMode} avatarUrl={(element as any).avatarUrl} showDuration={(element as any).showDuration} />
              </div>
            ) : (
              <audio
                controls
                className="w-full"
                src={element.url || undefined}
                preload="none"
                style={{ marginBottom: `${Math.max(mbRem * 0.5, 0.25)}rem`, pointerEvents: previewMode ? 'auto' : 'none' }}
              >
                Seu navegador não suporta o elemento de áudio.
              </audio>
            )}
            {element.question && (
              <p
                style={{
                  fontSize: config.fontSizes.subtitle,
                  fontFamily: config.fontFamily,
                  color: config.colors.text || '#374151',
                  marginTop: '0.5rem'
                }}
              >
                {interpolate(element.question)}
              </p>
            )}
          </div>
        );

      case ElementType.QA:
        const qaItems = (element as any).items || [];
        const qaAllowMultiple = (element as any).allowMultiple || false;
        const qaOpenFirst = (element as any).openFirst || false;
        const qaBorder = (element as any).border || 'small';
        const qaShadow = (element as any).shadow || 'none';
        const qaBorderClass = qaBorder === 'none' ? 'border-0' : qaBorder === 'small' ? 'border' : qaBorder === 'medium' ? 'border-2' : 'border-4';
        const qaShadowClass = qaShadow === 'none' ? '' : qaShadow === 'small' ? 'shadow-sm' : qaShadow === 'medium' ? 'shadow' : 'shadow-lg';
        const [openSet, setOpenSet] = React.useState<number[]>(qaOpenFirst && qaItems.length > 0 ? [0] : []);
        React.useEffect(() => {
          setOpenSet(qaOpenFirst && qaItems.length > 0 ? [0] : []);
        }, [element.id, qaOpenFirst, qaItems.length]);
        const isOpen = (idx: number) => openSet.includes(idx);
        const toggleIdx = (idx: number) => {
          if (!previewMode) return;
          setOpenSet(prev => {
            const active = prev.includes(idx);
            if (qaAllowMultiple) {
              return active ? prev.filter(i => i !== idx) : [...prev, idx];
            }
            return active ? [] : [idx];
          });
        };
        if (previewMode && !isDelayVisible) return null;
        return (
          <div className={`${qaBorderClass} border-slate-300 bg-white ${qaShadowClass}`} style={{ borderRadius: globalRadius, marginBottom: `${mbRem}rem` }}>
            {qaItems.map((qa: any, idx: number) => (
              <div key={idx} className={idx > 0 ? 'border-t border-slate-200' : ''}>
                <button
                  type="button"
                  onClick={() => toggleIdx(idx)}
                  className="w-full flex items-center justify-between px-4 py-3"
                  style={{ fontFamily: config.fontFamily, fontSize: config.fontSizes.option, color: '#0f172a', pointerEvents: previewMode ? 'auto' : 'none' }}
                >
                  <span style={{ fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: interpolate(qa.question || '') }} />
                  <span className="ml-3 text-slate-500" aria-hidden>
                    {isOpen(idx) ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 15l6-6 6 6" /></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
                    )}
                  </span>
                </button>
                {isOpen(idx) && (
                  <div className="px-4 pb-4 text-slate-700" style={{ fontFamily: config.fontFamily }}>
                    <div style={{ fontSize: config.fontSizes.subtitle, whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: interpolate(qa.answer || '') }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case ElementType.Video:
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            {(element as any).title && (
              <h4
                className="font-medium text-center"
                style={{
                  fontSize: config.fontSizes.subtitle,
                  fontFamily: config.fontFamily,
                  color: config.colors.text || '#374151',
                  marginBottom: '0.5rem'
                }}
              >
                {interpolate((element as any).title || '')}
              </h4>
            )}
            <div className="relative w-full max-w-2xl mx-auto">
              {((element as any).url) ? (
                (() => {
                  let finalUrl = (element as any).url || '';

                  // Se parecer HTML (ex: VTurb, embed codes), renderizar como HTML Raw
                  if (finalUrl.trim().startsWith('<')) {
                    return (
                      <EmbedRenderer code={finalUrl} className="w-full" previewMode={previewMode} aspectRatio={(element as any).aspectRatio || '16:9'} />
                    );
                  }

                  const isAutoplay = (element as any).autoplay === true;
                  const isClean = (element as any).cleanPlayer === true;
                  const hideInfo = (element as any).hideInfo === true;

                  const separator = finalUrl.includes('?') ? '&' : '?';
                  let params = '';

                  if (isAutoplay && !finalUrl.includes('autoplay=')) {
                    params += 'autoplay=1&muted=1&';
                  }

                  if (isClean) {
                    // Parâmetros para limpar o player do YouTube
                    if (!finalUrl.includes('controls=')) params += 'controls=0&';
                    if (!finalUrl.includes('modestbranding=')) params += 'modestbranding=1&';
                    if (!finalUrl.includes('rel=')) params += 'rel=0&';
                    if (!finalUrl.includes('showinfo=')) params += 'showinfo=0&';
                    if (!finalUrl.includes('iv_load_policy=')) params += 'iv_load_policy=3&';
                  }

                  if (params) {
                    finalUrl += separator + params.slice(0, -1); // Remove o último &
                  }

                  // Calcular aspect ratio baseado na configuração
                  const videoAspectRatio = (element as any).aspectRatio || '16:9';
                  const paddingBottomMap: Record<string, string> = {
                    '16:9': '56.25%',
                    '9:16': '177.78%',
                    '4:3': '75%',
                    '1:1': '100%'
                  };

                  // Estilos do container
                  const containerStyle: React.CSSProperties = {
                    position: 'relative',
                    width: '100%',
                    paddingBottom: paddingBottomMap[videoAspectRatio] || '56.25%',
                    overflow: 'hidden',
                    borderRadius: globalRadius,
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    // Para vídeos verticais, limitar largura para não ficar gigante
                    ...(videoAspectRatio === '9:16' ? { maxWidth: '360px', margin: '0 auto' } : {})
                  };

                  const iframeStyle: React.CSSProperties = hideInfo ? {
                    position: 'absolute',
                    top: '-25%', // Sobe o vídeo para esconder o topo
                    left: '0',
                    width: '100%',
                    height: '150%', // Aumenta a altura para compensar o corte
                    border: 'none'
                  } : {
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  };

                  return (
                    <div style={containerStyle}>
                      <iframe
                        src={finalUrl}
                        allow={(element as any).allow || `accelerometer; ${isAutoplay ? 'autoplay; ' : ''}clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                        allowFullScreen
                        referrerPolicy={(element as any).referrerPolicy}
                        title={interpolate((element as any).title || 'Vídeo')}
                        style={iframeStyle}
                      />
                      {/* No editor: overlay transparente para capturar cliques e permitir seleção do elemento */}
                      {!previewMode && (
                        <div
                          className="absolute inset-0 z-10 cursor-pointer"
                          style={{ background: 'transparent' }}
                        />
                      )}
                    </div>
                  );
                })()
              ) : (
                <div
                  className="w-full aspect-video border border-slate-300 flex items-center justify-center"
                  style={{ borderRadius: globalRadius, backgroundColor: '#f1f5f9' }}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <polygon points="9,8 16,12 9,16" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        );

      case ElementType.Button:
        console.log('🔵 [ElementRenderer] Renderizando botão:', {
          id: element.id,
          previewMode,
          isDelayVisible,
          willRender: !(previewMode && !isDelayVisible),
          action: (element as any).action,
          redirectUrl: (element as any).redirectUrl
        });
        const widthPercent = (element as any).widthPercent ?? 100;
        const btnAlign = (element as any).align || 'center';
        const btnJustify = btnAlign === 'left' ? 'flex-start' : btnAlign === 'right' ? 'flex-end' : 'center';
        const btnHeight = (element as any).height || config.elementSize || 'medium';
        const btnPad = (btnHeight === 'small' ? '0.5rem 0.75rem' : btnHeight === 'medium' ? '0.75rem 1rem' : btnHeight === 'large' ? '1rem 1.25rem' : '1.25rem 1.5rem');
        const btnAnim = (element as any).animation || 'none';
        const animClass = btnAnim === 'zoom'
          ? 'transition-transform duration-200 hover:scale-[1.04] active:scale-[0.98]'
          : btnAnim === 'pulse'
            ? 'relative btn-zoom-loop btn-glow-pulse'
            : btnAnim === 'bounce'
              ? 'btn-bounce-loop'
              : btnAnim === 'radar'
                ? 'relative'
                : '';
        { (() => { const id = 'btn-extras-style'; if (!document.getElementById(id)) { const el = document.createElement('style'); el.id = id; el.textContent = `.btn-radar::after { content: ''; position: absolute; inset: -2px; border-radius: inherit; box-shadow: 0 0 0 0 rgba(var(--btn-color-rgb, 59,130,246), 0.35); animation: btn-radar-pulse 1.8s ease-out infinite; pointer-events: none; } @keyframes btn-radar-pulse { 0% { box-shadow: 0 0 0 0 rgba(var(--btn-color-rgb,59,130,246),0.35); } 70% { box-shadow: 0 0 0 14px rgba(var(--btn-color-rgb,59,130,246),0); } 100% { box-shadow: 0 0 0 0 rgba(var(--btn-color-rgb,59,130,246),0); } } .btn-emboss::before { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: var(--btn-emboss-h, 0.6rem); background: rgba(var(--btn-color-dark-rgb, 0,0,0), 0.45); border-bottom-left-radius: inherit; border-bottom-right-radius: inherit; pointer-events: none; } .btn-zoom-loop { animation: btn-zoom-loop 1.1s ease-in-out infinite; } @keyframes btn-zoom-loop { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.055); } } .btn-glow-pulse::after { content: ''; position: absolute; inset: 0; border-radius: inherit; box-shadow: 0 8px 22px rgba(var(--btn-color-rgb,59,130,246), 0.30); animation: btn-glow-pulse 1.4s ease-in-out infinite; pointer-events: none; } @keyframes btn-glow-pulse { 0%, 100% { box-shadow: 0 6px 18px rgba(var(--btn-color-rgb,59,130,246), 0.26); } 50% { box-shadow: 0 10px 28px rgba(var(--btn-color-rgb,59,130,246), 0.42); } } .btn-bounce-loop { animation: btn-bounce-loop 3.6s cubic-bezier(.215,.61,.355,1) infinite; transform-origin: center; } @keyframes btn-bounce-loop { 0% { transform: translateY(0); } 10% { transform: translateY(12%); } 22% { transform: translateY(-9%); } 30% { transform: translateY(5%); } 36% { transform: translateY(-3%); } 40%, 100% { transform: translateY(0); } }`; document.head.appendChild(el); } })() }
        const toRgb = (hex: string) => {
          const h = (hex || '').replace('#', '');
          const m = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
          const r = parseInt(m.slice(0, 2), 16) || 0;
          const g = parseInt(m.slice(2, 4), 16) || 0;
          const b = parseInt(m.slice(4, 6), 16) || 0;
          return `${r}, ${g}, ${b}`;
        };
        const toRgbObj = (hex: string) => {
          const h = (hex || '').replace('#', '');
          const m = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
          return {
            r: parseInt(m.slice(0, 2), 16) || 0,
            g: parseInt(m.slice(2, 4), 16) || 0,
            b: parseInt(m.slice(4, 6), 16) || 0,
          };
        };
        const darkenRgb = (rgb: { r: number; g: number; b: number }, amt = 40) => `${Math.max(0, rgb.r - amt)}, ${Math.max(0, rgb.g - amt)}, ${Math.max(0, rgb.b - amt)}`;
        const btnColor = (element as any).color || config.colors.primary;
        const btnStyleVariant = (element as any).styleVariant || 'normal';
        const btnShadowClass = btnStyleVariant === 'sombra' ? 'shadow-lg' : btnStyleVariant === 'alto' ? 'shadow-none' : 'shadow-none';
        const baseBg = btnColor;
        const baseBgImage = 'none';
        const extraBoxShadow = btnStyleVariant === 'sombra' ? '0 8px 20px rgba(0,0,0,0.25)' : btnStyleVariant === 'alto' ? 'inset 0 1px 0 rgba(255,255,255,0.6)' : 'none';
        if (previewMode && !isDelayVisible) {
          console.log('⚠️ [ElementRenderer] Botão não renderizado devido a previewMode e isDelayVisible');
          return null;
        }
        const btnAction = (element as any).action || 'next';
        const redirectUrl = (element as any).redirectUrl || '';
        return (
          <div className="flex flex-col" style={{ marginBottom: `${mbRem}rem`, marginTop: '1.5rem', alignItems: btnJustify }}>
            {Array.isArray(invalidFieldIds) && invalidFieldIds.length > 0 && (
              <div
                className="w-full mb-3 px-4 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg text-center"
                style={{ fontFamily: config.fontFamily }}
              >
                {validationMessage || 'Preencha os campos obrigatórios para continuar'}
              </div>
            )}
            <button
              disabled={isRedirecting && btnAction === 'redirect' && (element.target || '_self') === '_self'}
              className={`text-white font-semibold ${btnShadowClass} transition-all duration-200 active:scale-[0.99] ${animClass} ${(btnAnim === 'radar' ? 'btn-radar ' : '') + (btnStyleVariant === 'alto' ? 'btn-emboss relative overflow-hidden' : '')} ${isRedirecting && btnAction === 'redirect' && (element.target || '_self') === '_self' ? 'opacity-50 cursor-not-allowed' : ''} ${shake && Array.isArray(invalidFieldIds) && invalidFieldIds.length > 0 ? 'input-shake' : ''}`}
              style={{
                backgroundColor: baseBg,
                backgroundImage: baseBgImage,
                fontSize: scaledOptionFont,
                fontFamily: config.fontFamily,
                width: `${widthPercent}%`,
                padding: btnPad,
                borderRadius: globalRadius,
                boxShadow: extraBoxShadow,
                filter: isRedirecting && btnAction === 'redirect' && (element.target || '_self') === '_self' ? 'saturate(50%)' : 'saturate(105%)',
                ['--btn-color-rgb' as any]: toRgb(btnColor),
                ['--btn-color-dark-rgb' as any]: darkenRgb(toRgbObj(btnColor)),
                ['--btn-emboss-h' as any]: (config.elementSize === 'small' ? '0.4rem' : config.elementSize === 'medium' ? '0.45rem' : config.elementSize === 'large' ? '0.5rem' : '0.55rem'),
                textAlign: 'center'
              }}
              onClick={(e) => {
                // Bloqueio imediato: desabilitar botão no DOM antes de qualquer processamento (só para _self)
                const button = e.currentTarget;
                const target = element.target || '_self';
                const shouldBlock = btnAction === 'redirect' && (isRedirecting || button.disabled) && target === '_self';

                if (shouldBlock) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }

                // Desabilitar botão IMEDIATAMENTE no DOM (antes de async) - só para _self
                if (btnAction === 'redirect' && target === '_self') {
                  button.disabled = true;
                  button.style.opacity = '0.5';
                  button.style.cursor = 'not-allowed';
                  button.style.pointerEvents = 'none';
                }

                const url = String(redirectUrl || '').trim();

                if (btnAction === 'redirect' && url && onButtonAction) {
                  // Só fazer redirect se há handler (não no editor)
                  // Passar informações adicionais incluindo target
                  const target = element.target || '_blank';
                  onButtonAction('redirect', url, { target });
                  return;
                }

                const fn = (element as any).onClick;
                if (typeof fn === 'function') { fn(); return; }

                // Priorizar onNext (que contém validação) sobre onButtonAction('next')
                if (onNext) {
                  onNext();
                } else if (onButtonAction) {
                  onButtonAction('next');
                }
              }}
            >
              {isRedirecting && btnAction === 'redirect' && (element.target || '_self') === '_self' ? (
                <svg className="animate-spin h-5 w-5 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                interpolate((element as any).text || 'Continuar')
              )}
            </button>
          </div>
        );

      case ElementType.Spacer:
        if (previewMode && !isDelayVisible) return null;
        return <div style={{ height: `${element.height || 20}px` }} />;

      case ElementType.Loading:
        const duration = (element as any).durationSeconds || 10;
        const autoAfter = (element as any).autoProceedAfterSeconds;
        const loadingBarColor = (typeof (element as any).barColor === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((element as any).barColor)) ? (element as any).barColor : config.colors.primary;
        const [progress, setProgress] = React.useState(0);
        const [done, setDone] = React.useState(false);
        const [stripeOffset, setStripeOffset] = React.useState(0);
        const [currentSubtitleIndex, setCurrentSubtitleIndex] = React.useState(0);

        // Get subtitles array (fallback to single subtitle for backwards compatibility)
        const subtitlesArray: string[] = React.useMemo(() => {
          if ((element as any).subtitles && (element as any).subtitles.length > 0) {
            return (element as any).subtitles.filter((s: string) => s && s.trim());
          }
          if ((element as any).subtitle) {
            return [(element as any).subtitle];
          }
          return [];
        }, [(element as any).subtitles, (element as any).subtitle]);

        React.useEffect(() => {
          setProgress(0);
          setDone(false);
          setCurrentSubtitleIndex(0);
          const totalMs = Math.max(0.1, duration) * 1000;
          const start = performance.now();
          let raf: number;
          const tick = (now: number) => {
            const elapsed = now - start;
            const pct = Math.min(100, (elapsed / totalMs) * 100);
            setProgress(pct);
            if (pct < 100) {
              raf = requestAnimationFrame(tick);
            } else {
              setDone(true);
            }
          };
          raf = requestAnimationFrame(tick);
          return () => cancelAnimationFrame(raf);
        }, [element.id, (element as any).durationSeconds]);

        // Animated subtitle cycling effect
        React.useEffect(() => {
          if (subtitlesArray.length <= 1) return;
          const totalMs = Math.max(0.1, duration) * 1000;
          const intervalMs = totalMs / subtitlesArray.length;

          const interval = setInterval(() => {
            setCurrentSubtitleIndex((prev) => {
              const next = prev + 1;
              return next >= subtitlesArray.length ? subtitlesArray.length - 1 : next;
            });
          }, intervalMs);

          return () => clearInterval(interval);
        }, [element.id, duration, subtitlesArray.length]);

        React.useEffect(() => {
          let raf: number;
          let last = performance.now();
          const animate = (now: number) => {
            const dt = now - last;
            last = now;
            setStripeOffset((prev) => (prev + dt * 0.15) % 40);
            raf = requestAnimationFrame(animate);
          };
          raf = requestAnimationFrame(animate);
          return () => cancelAnimationFrame(raf);
        }, []);
        React.useEffect(() => {
          if (!previewMode) return;
          if (!done) return;
          const wait = typeof autoAfter === 'number' ? Math.max(0, autoAfter) * 1000 : null;
          if (wait == null) return;
          const t = setTimeout(() => {
            if (onNext) onNext();
          }, wait);
          return () => clearTimeout(t);
        }, [done, autoAfter, onNext, previewMode]);
        const radius = globalRadius;
        const showPercent = (element as any).showPercent !== false;
        const currentSubtitle = subtitlesArray[currentSubtitleIndex] || '';
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            {((element as any).title || currentSubtitle) && (
              <div className="text-center" style={{ marginBottom: '1.5rem' }}>
                {(element as any).title && (
                  <div
                    className="font-bold"
                    style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 1.15})`, fontFamily: config.fontFamily, color: config.colors.title || config.colors.text || '#374151', marginBottom: '1rem' }}
                  >
                    {interpolate((element as any).title || '')}
                  </div>
                )}
                {currentSubtitle && (
                  <div
                    key={currentSubtitleIndex}
                    className="text-slate-600"
                    style={{
                      fontSize: scaledOptionFont,
                      fontFamily: config.fontFamily,
                      animation: subtitlesArray.length > 1 ? 'subtitle-fade-in 0.4s ease-out' : undefined
                    }}
                    dangerouslySetInnerHTML={{ __html: interpolate(currentSubtitle) }}
                  />
                )}
                {subtitlesArray.length > 1 && (
                  <style>{`
                    @keyframes subtitle-fade-in {
                      0% { opacity: 0; transform: translateY(8px); }
                      100% { opacity: 1; transform: translateY(0); }
                    }
                  `}</style>
                )}
              </div>
            )}
            <div className="relative w-full bg-slate-200 rounded-full overflow-hidden" style={{ borderRadius: radius, height: '1rem' }}>
              <div
                className="absolute left-0 top-0 bottom-0 transition-transform duration-100 ease-linear"
                style={{
                  width: '100%',
                  transformOrigin: 'left',
                  transform: `scaleX(${Math.max(0, Math.min(1, progress / 100))})`,
                  backgroundColor: loadingBarColor,
                  backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,.35), rgba(255,255,255,.1)), repeating-linear-gradient(45deg, rgba(255,255,255,.35) 0px, rgba(255,255,255,.35) 10px, rgba(255,255,255,.2) 10px, rgba(255,255,255,.2) 20px)`,
                  backgroundSize: 'auto, 40px 40px',
                  backgroundPosition: `center, ${-stripeOffset}px 0`
                }}
              />
              {showPercent && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-semibold" style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale * 0.7})`, fontFamily: config.fontFamily, color: '#0f172a' }}>
                    {`${Math.round(progress)}%`}
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      case ElementType.Testimonial:
        const tBorderSize = (element as any).border || 'small';
        const tShadowSize = (element as any).shadow || 'small';
        const tBorderClass = tBorderSize === 'none' ? 'border-0' : tBorderSize === 'small' ? 'border' : tBorderSize === 'medium' ? 'border-2' : 'border-4';
        const tShadowClass = tShadowSize === 'none' ? '' : tShadowSize === 'small' ? 'shadow-sm' : tShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const tStars = Math.max(0, Math.min(5, (element as any).stars || 0));
        const tName = (element as any).name || '';
        const tInfo = (element as any).info || '';
        const tText = (element as any).text || '';
        const tImageUrl = (element as any).imageUrl || '';
        const tStarColor = (element as any).starColor || '#f59e0b';
        if (previewMode && !isDelayVisible) return null;
        return (
          <div className={`bg-white ${tBorderClass} border-slate-200 ${tShadowClass}`} style={{ borderRadius: globalRadius, marginBottom: `${mbRem}rem` }}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                {tImageUrl ? (
                  <img src={tImageUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : null}
                <div className="flex-1">
                  <div className="flex items-center gap-0.5 mb-1" style={{ color: tStarColor }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className={i < tStars ? '' : 'text-slate-300'}>★</span>
                    ))}
                  </div>
                  {tName && (
                    <div className="text-slate-800 font-semibold" style={{ fontSize: `calc(${config.fontSizes.option} * ${sizeScale})`, fontFamily: config.fontFamily }}>
                      {tName}
                    </div>
                  )}
                  {tInfo && (
                    <div className="text-slate-500 text-sm" style={{ fontFamily: config.fontFamily }}>
                      {tInfo}
                    </div>
                  )}
                  {tText && (
                    <p className="mt-2 text-slate-700" style={{ fontSize: config.fontSizes.subtitle, fontFamily: config.fontFamily }}>
                      {tText}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case ElementType.Form:
        const fAlign = (element as any).align || 'left';
        const fStyle = (element as any).style || 'boxed';
        const isFlat = fStyle === 'flat';

        const fBorderSize = (element as any).border || 'small';
        const fShadowSize = (element as any).shadow || 'none';
        const fBorderClass = fBorderSize === 'none' ? 'border-0' : fBorderSize === 'small' ? 'border' : fBorderSize === 'medium' ? 'border-2' : 'border-4';
        const fShadowClass = fShadowSize === 'none' ? '' : fShadowSize === 'small' ? 'shadow-sm' : fShadowSize === 'medium' ? 'shadow' : 'shadow-lg';
        const fFont = (element as any).fontSize || 'normal';
        const inputFontSize = (fFont === 'normal') ? config.fontSizes.subtitle : (fFont === 'large') ? config.fontSizes.option : `calc(${config.fontSizes.option} * 1.15)`;
        const labelPosRaw = (element as any).labelPosition || 'tradicional';
        const labelPos = isFlat ? (labelPosRaw === 'none' ? 'none' : 'tradicional') : (labelPosRaw === 'internal' ? 'tradicional' : labelPosRaw);
        const key = String(element.id);
        const fieldKey = typeof (element as any).fieldId === 'string' ? (element as any).fieldId.trim() : '';
        const [localVal, setLocalVal] = React.useState<string>('');

        const getVal = (k: string) => {
          const v = formValues?.[k];
          if (v && typeof v === 'object' && 'value' in v) return v.value;
          return v;
        };

        const curVal = (fieldKey ? getVal(fieldKey) : getVal(key)) || localVal;

        const setVal = (next: string) => {
          const sanitized = sanitizeInput(next);
          const valueObj = {
            value: sanitized,
            customId: fieldKey || key,
            internalId: key,
            fieldType: (element as any).fieldType || 'text'
          };

          const nextMap = { ...(formValues || {}), [key]: valueObj };
          if (fieldKey) nextMap[fieldKey] = valueObj;

          if (onFormChange) onFormChange(nextMap); else setLocalVal(sanitized);
        };

        const applyMask = (value: string, mask: string) => {
          const digits = (value || '').replace(/\D+/g, '');
          if (digits.length === 0) return '';
          let out = '';
          let d = 0;
          for (let i = 0; i < mask.length; i++) {
            const m = mask[i];
            if (m === '9') {
              if (d < digits.length) {
                out += digits[d++];
              } else {
                break;
              }
            } else {
              out += m;
            }
          }
          return out.replace(/[^0-9]+$/, '');
        };
        const t = (element as any).fieldType || 'text';
        const mapType = (tt: string) => (tt === 'email' ? 'email' : tt === 'number' ? 'text' : tt === 'date' ? 'date' : tt === 'phone' ? 'tel' : 'text');
        const mapInputMode = (tt: string) => (tt === 'number' || tt === 'phone' ? 'numeric' : undefined);
        const mask = typeof (element as any).mask === 'string' ? (element as any).mask : '';
        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value || '';
          const isNumber = t === 'number';
          const base = isNumber ? raw.replace(/\D+/g, '') : raw;
          const nextVal = mask ? applyMask(base, mask) : base;
          setVal(nextVal);
        };
        const placeholderValue = disableInterpolation ? ((element as any).placeholder || '') : interpolate((element as any).placeholder || '');
        const labelHtml = interpolate((element as any).title || '');
        const labelAlignStyle = fAlign === 'center' ? { textAlign: 'center' as const } : fAlign === 'right' ? { textAlign: 'right' as const } : { textAlign: 'left' as const };
        const labelOverlayPos = fAlign === 'center' ? { left: '50%', transform: 'translateX(-50%)' } : fAlign === 'right' ? { right: '0.5rem' } : { left: '0.5rem' };
        const isInvalid = Array.isArray(invalidFieldIds) && (
          (fieldKey ? invalidFieldIds.includes(fieldKey) : false) || invalidFieldIds.includes(key)
        );
        if (previewMode && !isDelayVisible) return null;
        return (
          <div style={{ marginBottom: `${mbRem}rem` }}>
            <div className="space-y-2" style={{ textAlign: fAlign }}>
              <div className={isFlat ? 'relative' : `${fBorderClass} border-slate-300 ${fShadowClass} bg-white relative`} style={{ borderRadius: isFlat ? 0 : globalRadius }}>
                {labelPos === 'floating' ? (
                  <div className="absolute -top-2 px-2 text-xs rounded bg-white/80 text-slate-700 border border-slate-300" style={{ ...labelOverlayPos, zIndex: 1, fontFamily: config.fontFamily }} dangerouslySetInnerHTML={{ __html: labelHtml }} />
                ) : null}
                <div className={isFlat ? '' : "px-4 py-3"}>
                  {labelPos === 'tradicional' ? (
                    <label
                      className={isFlat ? "block font-medium text-slate-700 mb-2" : "block text-sm font-medium text-slate-700 mb-1"}
                      style={{ fontFamily: config.fontFamily, fontSize: isFlat ? config.fontSizes.option : undefined }}
                    >
                      <span className="align-middle" dangerouslySetInnerHTML={{ __html: labelHtml }} />
                    </label>
                  ) : null}
                  <div className="relative">

                    <input
                      type={mapType(t)}
                      inputMode={mapInputMode(t) as any}
                      value={curVal}
                      onChange={onChange}
                      placeholder={placeholderValue}
                      required={(element as any).required === true}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-sky-500 ${isInvalid ? 'input-error' : 'border-slate-300 focus:border-sky-500'} ${isInvalid && shake ? 'input-shake' : ''}`}
                      style={{ fontSize: inputFontSize, fontFamily: config.fontFamily, textAlign: fAlign as any }}
                    />
                    {disableInterpolation && /\{[a-zA-Z0-9_-]+\}/.test(((element as any).placeholder || '')) ? (
                      <div className="absolute right-3 bottom-2 flex gap-1 pointer-events-none">
                        {Array.from(new Set((((element as any).placeholder || '').match(/\{[a-zA-Z0-9_-]+\}/g) || []))).map((tok, i) => (
                          <span key={i} className="text-xs" style={{ backgroundColor: 'rgba(14,165,233,0.16)', color: '#0ea5b7', padding: '0 6px', borderRadius: '6px', border: '1px dashed #0ea5b7' }}>{tok}</span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              {labelPos === 'none' ? (
                <div style={labelAlignStyle}></div>
              ) : null}
            </div>
          </div>
        );

      case ElementType.FbComments:
        return (
          <FbCommentsRenderer
            element={element}
            previewMode={previewMode}
            isDelayVisible={isDelayVisible}
            mbRem={mbRem}
            globalRadius={globalRadius}
            interpolate={interpolate}
          />
        );



      default:
        return <div>Elemento desconhecido</div>;
    }
  };

  return renderElement();
};

export default ElementRenderer;
{ (() => { const id = 'form-error-style'; if (!document.getElementById(id)) { const el = document.createElement('style'); el.id = id; el.textContent = `@keyframes form-shake { 0%,100%{ transform: translateX(0) } 20%{ transform: translateX(-4px) } 40%{ transform: translateX(4px) } 60%{ transform: translateX(-3px) } 80%{ transform: translateX(3px) } } .input-error { border-color: #ef4444 !important; } .input-shake { animation: form-shake 320ms ease-in-out; }`; document.head.appendChild(el); } })() }
